<template>
  <div>
    <div
      class="protected-page"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <div v-if="!showSplitDetails" class="shadow-box-main">
        <div class="bank-ledger-title">
          Bank Transactions
          <div class="title-container">
            <div class="account-selection">
              <label style="color: #fff" for="account-select"
                >Select Account:</label
              >
              <select
                id="account-select"
                v-model="selectedAccount"
                @change="handleAccountChange"
              >
                <option
                  v-for="account in accountOptions"
                  :key="account.accountNumber"
                  :value="account.accountNumber"
                >
                  {{ account.accountNumber }} : {{ account.accountName }}
                </option>
              </select>
              <button @click="confirmAccount">Select</button>
            </div>
          </div>
        </div>
        <div class="search-container">
          <div>
            <input
              v-model="rawSearchQuery"
              ref="searchQuery"
              class="search-input"
              :class="{ pending: rawSearchQuery !== searchQuery }"
              type="text"
              placeholder="Search..."
              @keydown="handleSearchInputKeydown"
              :title="rawSearchQuery"
            />
            <button
              @click="clearSearch"
              class="clear-button"
              title="click to clear the search input (or press ESC)"
            >
              <i class="fa fa-times-circle"></i>
            </button>
            <button
              title="click for search input help"
              @click="searchHelp = !searchHelp"
              class="clear-button"
            >
              <i class="fa fa-question-circle"></i>
            </button>
            <div
              class="row-count"
              style="display: inline-block; min-width: 160px"
            >
              showing : {{ visibleTransactionCount }} of {{ totalRows }} rows
            </div>
            <span class="from-to-label">
              <div class="month-shortcuts">
                <button
                  v-for="yearOffset in [2, 1, 0]"
                  :key="'year-' + yearOffset"
                  class="year-button"
                  :class="{
                    'year-selected':
                      selectedYear === new Date().getFullYear() - yearOffset,
                  }"
                  @click="setYearRange(new Date().getFullYear() - yearOffset)"
                  :title="`Select year ${new Date().getFullYear() - yearOffset}`"
                >
                  {{ String(new Date().getFullYear() - yearOffset).slice(-2) }}
                </button>
                <span class="year-month-gap"></span>
                <button
                  v-for="(month, index) in monthShortcuts"
                  :key="index"
                  class="month-button"
                  @click="setMonthRange(index)"
                  :title="`Set date range to ${month.name} ${selectedYear}`"
                >
                  {{ month.short }}
                </button>
              </div>
              <input
                class="date-picker"
                type="text"
                placeholder="MM/DD/YY"
                :value="formatDateShort(ledgerFromDate)"
                @input="updateFromDate"
                @blur="validateFromDate"
              />
              to
              <input
                class="date-picker"
                type="text"
                placeholder="MM/DD/YY"
                :value="formatDateShort(ledgerToDate)"
                @input="updateToDate"
                @blur="validateToDate"
              />
              <button
                title="reset date range"
                class="clear-button"
                @click="resetDateRange('ytd')"
              >
                YT
              </button>
              <button
                title="reset date range"
                class="clear-button"
                @click="resetDateRange"
              >
                <i style="font-size: 0.8em" class="fa fa-calendar"></i>
              </button>
              <button class="clear-button" @click="fetchTransactions">
                <i style="font-size: 0.8em" class="fa fa-sync"></i>
              </button>
            </span>

            <button
              title="Autoselect Credits within period"
              class="select-credits-button"
              @click="selectCredits"
            >
              <i class="fas fa-magic-wand-sparkles"></i>
            </button>
            <button
              title="show chart"
              class="select-credits-button"
              @click="showChart"
            >
              <i class="fas fa-chart-line"></i>
            </button>
            <button
              title="print friendly"
              :class="
                isPrinterFriendly
                  ? 'select-credits-button-faded'
                  : 'select-credits-button'
              "
              @click="printerFriendly"
            >
              PF
            </button>
            <button title="print" class="select-credits-button" @click="print">
              <i class="fas fa-print"></i>
            </button>
            <button
              title="unmatched"
              :class="[
                'select-credits-button',
                { 'selected-button': filteringUnmatched },
              ]"
              @click="findUnmatched"
            >
              <i class="fas fa-chain-broken"></i>
            </button>
            <button
              title="add to ledger"
              class="select-credits-button"
              @click="addToLedger"
            >
              <i class="fas fa-exchange"></i>
            </button>
            <button
              title="create security deposit ledger entry"
              class="select-credits-button"
              @click="createSecurityDepositLedger"
            >
              <i class="fas fa-code-fork"></i>
            </button>
            <button
              title="unmatched"
              class="select-credits-button"
              @click="exportTransactions"
            >
              <i class="fas fa-file-excel"></i>
            </button>
            <button
              title="verify data errors"
              class="select-credits-button"
              @click="setVerifySearch"
            >
              <i class="fas fa-check"></i>
            </button>
            <button
              title="share current search"
              class="select-credits-button"
              @click="shareCurrentSearch"
            >
              <i class="fas fa-share-nodes"></i>
            </button>
          </div>
          <div class="totals-wrapper">
            <span class="selected-row-totals">
              <span v-if="selectedRowTotals.count > 0">
                R={{ selectedRowTotals.count }}
              </span>
              <span v-if="selectedRowTotals.totalCredits > 0">, credit: </span>
              {{ formatNumber(selectedRowTotals.totalCredits) }}
              <span v-if="selectedRowTotals.totalDebits < 0">, debit: </span>
              {{ formatNumber(selectedRowTotals.totalDebits) }}
            </span>
            <button @click="clearSelection" class="clear-button">
              <i class="fa fa-minus-circle"></i>
            </button>
          </div>
        </div>
        <div
          class="table-container"
          ref="tableContainer"
          @mouseenter="handleTableEnter"
          @mouseleave="handleTableLeave"
        >
          <!-- Verify tooltip positioned above Bank Check column -->
          <div
            v-if="showVerifyTooltip"
            class="verify-tooltip"
            @click="showVerifyTooltip = false"
          >
            Hover over the <b>Bank Check</b> column<br />to see the verify
            failure reason
            <span class="tooltip-close">×</span>
          </div>

          <table id="printableTable" class="table">
            <thead>
              <tr>
                <th
                  v-for="header in headers"
                  :key="header"
                  :class="{
                    'right-align':
                      header === 'Credit' ||
                      header === 'Debit' ||
                      header === 'Bal',
                    'wide-column': header === 'Bank Description',
                    'center-align':
                      header === 'Type' ||
                      header === 'CheckNo.' ||
                      header === 'N',
                    'sortable-header':
                      header === 'Bank Description' || header === 'Account',
                  }"
                  @click="
                    header === 'Bank Description'
                      ? toggleDescriptionSort()
                      : header === 'Account'
                        ? toggleAccountSort()
                        : null
                  "
                >
                  {{ header }}
                  <i
                    v-if="header === 'Bank Description'"
                    :class="
                      sortByDescription ? 'fa fa-sort-alpha-asc' : 'fa fa-sort'
                    "
                    :style="{
                      marginLeft: '5px',
                      opacity: sortByDescription ? '1' : '0.7',
                      color: sortByDescription ? '#4a4a4a' : '#999',
                    }"
                  ></i>
                  <i
                    v-if="header === 'Account'"
                    :class="
                      sortByAccount ? 'fa fa-sort-alpha-asc' : 'fa fa-sort'
                    "
                    :style="{
                      marginLeft: '5px',
                      opacity: sortByAccount ? '1' : '0.7',
                      color: sortByAccount ? '#4a4a4a' : '#999',
                    }"
                  ></i>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(transaction, index) in filteredTransactions"
                @dblclick="
                  this.selectedBankTransaction = transaction;
                  handleDblClick(transaction);
                "
                :key="transaction.Id"
                :id="'row-' + transaction.Id"
                :class="{
                  even: index % 2 === 0,
                  odd: index % 2 !== 0,
                  'selected-row': isSelected(transaction.Id),
                }"
                @click="handleRowClick(transaction, $event)"
              >
                <td class="center-align">
                  <i
                    :class="
                      transaction.note
                        ? 'fas fa-sticky-note'
                        : 'far fa-sticky-note'
                    "
                    :style="{ color: transaction.note ? 'black' : 'lightgrey' }"
                    @click.stop="openNoteDialog(transaction)"
                    :title="
                      transaction.note
                        ? 'Click to edit note: ' + transaction.note
                        : 'Click to add note'
                    "
                  ></i>
                </td>
                <td
                  v-for="(value, key) in stripId(transaction)"
                  :key="key"
                  :title="
                    key === 'Type'
                      ? `${transaction.Id}${transaction.linkedLedgerId ? '\n' + transaction.linkedLedgerId : ''}`
                      : key === 'BankRefCheckNum' &&
                          verifyFailureReasons[transaction.Id]
                        ? verifyFailureReasons[transaction.Id]
                        : value
                          ? value
                          : ''
                  "
                  :class="{
                    'right-align':
                      key === 'Credit' || key === 'Debit' || key === 'Balance',
                    'center-align':
                      key === 'Type' ||
                      key === 'checkNumber' ||
                      key == 'BankType',
                    'wide-column': key === 'BankRefDesc',
                    'fixed-column':
                      key === 'BankRefNum' || key === 'BankRefCheckNum',
                  }"
                >
                  <div
                    v-if="
                      key === 'Credit' || key === 'Debit' || key == 'Balance'
                    "
                  >
                    <span v-html="highlightSearch(formatNumber(value))"></span>
                  </div>
                  <div
                    v-else-if="key === 'Account'"
                    :class="[
                      noWrapText ? '' : 'text-ellipsis-narrow',
                      transaction.IsSplitParent ? 'bold-text' : '',
                    ]"
                    :title="value ? value : ''"
                    v-html="highlightSearch(value)"
                  ></div>
                  <div
                    v-else-if="key === 'Memo'"
                    :class="[
                      noWrapText ? '' : 'text-ellipsis-narrow',
                      hasDepositGroup(value) ? 'deposit-group-link' : '',
                    ]"
                    :title="value ? value : ''"
                    v-html="highlightSearch(value)"
                    @dblclick="handleMemoDoubleClick(value)"
                  ></div>
                  <div
                    v-else-if="key === 'Class' || key === 'Description'"
                    :class="noWrapText ? '' : 'text-ellipsis-narrow'"
                    :title="value ? value : ''"
                    v-html="highlightSearch(value)"
                  ></div>
                  <div
                    v-else-if="key === 'BankRefDesc'"
                    :class="noWrapText ? '' : 'text-ellipsis'"
                    :title="value ? value : ''"
                    v-html="highlightSearch(value)"
                  ></div>
                  <div
                    v-else-if="key === 'BankRefCheckNum'"
                    :style="
                      transaction.BankType === 'CHK' &&
                      transaction.ledgerMatched !== 1
                        ? { color: '#999', fontStyle: 'italic' }
                        : {}
                    "
                    v-html="highlightSearch(value)"
                  ></div>
                  <div
                    v-else-if="key === 'ledgerDate'"
                    v-html="
                      transaction.matched === 0 ? '' : highlightSearch(value)
                    "
                  ></div>
                  <div v-else v-html="highlightSearch(value)"></div>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="nothing-found">
            <span class="action-link" @click="clearSearch">Clear</span> or
            <span @click="focusSearch" class="action-link">change</span> the
            search filter, or
            <span class="action-link" @click="expandSearch">expand</span>
            the search.
          </div>
        </div>
        <div class="transactions-footer">
          <div v-if="footerMessage != ''" class="footer-message">
            {{ footerMessage }}
          </div>
        </div>
      </div>
      <div style="margin-top: 20px"></div>

      <div v-if="searchHelp" class="help-overlay">
        <div class="search-help">
          <div class="search-help-header">
            <div class="tips">Tips for searching</div>
            <button class="close-button" @click="searchHelp = false">
              <i class="fas fa-close"></i>
            </button>
          </div>
          <div style="font-size: 0.8em; margin-bottom: 5px; margin-left: 12px">
            Set your date range, then use search patterns below. Combine
            multiple filters with commas. Use toolbar buttons for quick actions
            and keyboard shortcuts for efficiency.
          </div>
          <div class="table-wrapper">
            <table>
              <thead>
                <tr>
                  <td style="width: 140px">Pattern/Action</td>
                  <td>Description</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    colspan="2"
                    style="
                      background-color: #a898b7;
                      color: white;
                      font-weight: bold;
                      text-align: center;
                    "
                  >
                    SEARCH PATTERNS
                  </td>
                </tr>
                <tr>
                  <td>anything</td>
                  <td>
                    Search for any text, number, or phrase in transaction data
                  </td>
                </tr>
                <tr>
                  <td>term1, term2</td>
                  <td>
                    Chain filters: first find 'term1', then refine results by
                    'term2'
                  </td>
                </tr>
                <tr>
                  <td style="cursor: pointer" @dblclick="usePattern(':u')">
                    :u
                  </td>
                  <td>
                    Show only <strong>unmatched</strong> bank transactions (not
                    linked to ledger entries)
                  </td>
                </tr>
                <tr>
                  <td style="cursor: pointer" @dblclick="usePattern(':n')">
                    :n
                  </td>
                  <td>
                    Show only transactions with <strong>notes</strong>. Combine
                    with other terms.
                  </td>
                </tr>
                <tr>
                  <td style="cursor: pointer" @dblclick="usePattern(':verify')">
                    :verify
                  </td>
                  <td>
                    Find potential <strong>data errors</strong> (checking
                    account entries with wrong split status)
                  </td>
                </tr>
                <tr>
                  <td style="cursor: pointer" @dblclick="usePattern(':123')">
                    :123
                  </td>
                  <td>
                    Find transaction by specific
                    <strong>ID number</strong> (replace 123 with actual ID)
                  </td>
                </tr>
                <tr>
                  <td style="cursor: pointer" @dblclick="usePattern('!CREDIT')">
                    !term
                  </td>
                  <td>
                    <strong>Exclude</strong> transactions containing 'term' (use
                    ! before any search term)
                  </td>
                </tr>
                <tr>
                  <td
                    colspan="2"
                    style="
                      background-color: #a898b7;
                      color: white;
                      font-weight: bold;
                      text-align: center;
                    "
                  >
                    TOOLBAR BUTTONS
                  </td>
                </tr>
                <tr>
                  <td><i class="fas fa-magic-wand-sparkles"></i> Magic Wand</td>
                  <td>
                    Auto-select credit transactions from current billing cycle
                    (25th-today)
                  </td>
                </tr>
                <tr>
                  <td><i class="fas fa-chart-line"></i> Chart</td>
                  <td>
                    Display transaction trends and patterns in a visual chart
                  </td>
                </tr>
                <tr>
                  <td>PF</td>
                  <td>
                    <strong>Printer Friendly</strong> - optimize table layout
                    for printing
                  </td>
                </tr>
                <tr>
                  <td><i class="fas fa-print"></i> Print</td>
                  <td>Print current filtered transactions</td>
                </tr>
                <tr>
                  <td><i class="fas fa-chain-broken"></i> Unmatched</td>
                  <td>Toggle filter to show only unmatched transactions</td>
                </tr>
                <tr>
                  <td><i class="fas fa-exchange"></i> Add to Ledger</td>
                  <td>
                    Add selected bank transaction to the accounting ledger
                  </td>
                </tr>
                <tr>
                  <td><i class="fas fa-file-excel"></i> Export</td>
                  <td>Export filtered transactions to Excel spreadsheet</td>
                </tr>
                <tr>
                  <td><i class="fas fa-check"></i> Verify</td>
                  <td>Quick search for potential data integrity issues</td>
                </tr>
                <tr>
                  <td
                    colspan="2"
                    style="
                      background-color: #a898b7;
                      color: white;
                      font-weight: bold;
                      text-align: center;
                    "
                  >
                    KEYBOARD SHORTCUTS
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #e8f4fd; font-weight: bold">
                    $ key
                  </td>
                  <td style="background-color: #e8f4fd">
                    Open the <strong>Cash Position</strong> dialog (works
                    anywhere on this page)
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #e8f4fd; font-weight: bold">
                    U key
                  </td>
                  <td style="background-color: #e8f4fd">
                    Toggle <strong>unmatched</strong> transaction filter (mouse
                    must be over table)
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #e8f4fd; font-weight: bold">
                    A key
                  </td>
                  <td style="background-color: #e8f4fd">
                    <strong>Add to ledger</strong> - works when exactly one
                    transaction is selected
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #e8f4fd; font-weight: bold">
                    Shift + A
                  </td>
                  <td style="background-color: #e8f4fd">
                    Run <strong>Automatch Only</strong> (same as Financials
                    menu)
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #e8f4fd; font-weight: bold">
                    Shift + M
                  </td>
                  <td style="background-color: #e8f4fd">
                    <strong>Match deposit group</strong> — manually link one
                    selected transaction to a depositGroup (e.g. 071626.1)
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #e8f4fd; font-weight: bold">
                    Shift + I
                  </td>
                  <td style="background-color: #e8f4fd">
                    In <strong>unmatched</strong> mode: add selected bank rows as
                    split children of the one selected ledger parent, then open
                    the edit dialog
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #e8f4fd; font-weight: bold">
                    F key
                  </td>
                  <td style="background-color: #e8f4fd">
                    <strong>Find matching</strong> - search ledger for selected
                    transaction amount
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #e8f4fd; font-weight: bold">
                    ? key
                  </td>
                  <td style="background-color: #e8f4fd">
                    Toggle this <strong>help dialog</strong> (mouse must be over
                    table)
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #e8f4fd; font-weight: bold">
                    ESC key
                  </td>
                  <td style="background-color: #e8f4fd">
                    <strong>Clear search</strong> or refresh transactions if
                    search is empty
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #e8f4fd; font-weight: bold">
                    Ctrl + M
                  </td>
                  <td style="background-color: #e8f4fd">
                    <strong>Match transactions</strong> - link selected bank and
                    ledger entries
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #e8f4fd; font-weight: bold">
                    Ctrl + ↑/↓
                  </td>
                  <td style="background-color: #e8f4fd">
                    <strong>Scroll</strong> to top/bottom of transaction table
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #e8f4fd; font-weight: bold">
                    ↑/↓ keys
                  </td>
                  <td style="background-color: #e8f4fd">
                    <strong>Increment/Decrement</strong> numeric values in
                    search input (when search input has focus)
                  </td>
                </tr>
                <tr>
                  <td
                    colspan="2"
                    style="
                      background-color: #a898b7;
                      color: white;
                      font-weight: bold;
                      text-align: center;
                    "
                  >
                    SELECTION & NAVIGATION
                  </td>
                </tr>
                <tr>
                  <td>Click</td>
                  <td>Select single transaction</td>
                </tr>
                <tr>
                  <td>Ctrl + Click</td>
                  <td>Add/remove transaction from selection</td>
                </tr>
                <tr>
                  <td>Shift + Click</td>
                  <td>Select range of transactions</td>
                </tr>
                <tr>
                  <td>Double-click</td>
                  <td>Auto-search for matching amounts in ledger</td>
                </tr>
                <tr>
                  <td><i class="fas fa-sticky-note"></i> Note Icon</td>
                  <td>
                    Click to add/edit notes on transactions (black = has note,
                    gray = no note)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div>
        <!-- create a small confirmation popup with OK and Cancel buttons -->
        <div v-if="showConfirm" class="confirm-popup">
          <div v-html="confirmMessage" class="confirm-message"></div>
          <div class="confirm-buttons">
            <button @click="confirmAction(true)">OK</button>
            <button @click="showConfirm = false">Cancel</button>
          </div>
        </div>

        <!-- Simple info popup (OK only) -->
        <div v-if="showInfoPopup" class="confirm-popup">
          <div class="confirm-message">{{ infoPopupMessage }}</div>
          <div class="confirm-buttons">
            <button @click="showInfoPopup = false">OK</button>
          </div>
        </div>

        <!-- Automatch progress popup -->
        <div v-if="showAutomatchPopup" class="confirm-popup automatch-popup">
          <div class="confirm-message">
            <i
              v-if="automatchRunning"
              class="fas fa-spinner fa-spin"
              style="margin-right: 8px"
            ></i>
            {{ automatchPopupMessage }}
          </div>
        </div>

        <!-- Manual depositGroup match popup -->
        <div
          v-if="showDepositGroupMatchPopup"
          class="confirm-popup deposit-group-match-popup"
        >
          <div class="confirm-message">
            <h3>Match Deposit Group</h3>
            <p v-if="depositGroupMatchTransaction">
              Link transaction #{{ depositGroupMatchTransaction.Id }}
              <span v-if="depositGroupMatchTransaction.Credit">
                (${{
                  formatNumber(depositGroupMatchTransaction.Credit)
                }})
              </span>
              to a depositGroup:
            </p>
            <input
              ref="depositGroupMatchInput"
              v-model="depositGroupMatchInput"
              type="text"
              placeholder="e.g. 071626.1"
              class="deposit-group-match-input"
              :disabled="depositGroupMatchLoading"
              @keyup.enter="submitDepositGroupMatch"
            />
            <div v-if="depositGroupMatchError" class="deposit-group-match-error">
              {{ depositGroupMatchError }}
            </div>
          </div>
          <div class="confirm-buttons">
            <button
              :disabled="depositGroupMatchLoading"
              @click="submitDepositGroupMatch"
            >
              {{ depositGroupMatchLoading ? 'Matching...' : 'Match' }}
            </button>
            <button
              :disabled="depositGroupMatchLoading"
              @click="closeDepositGroupMatchPopup"
            >
              Cancel
            </button>
          </div>
        </div>

        <!-- Share URL popup -->
        <div v-if="showSharePopup" class="confirm-popup">
          <div class="confirm-message">
            <h3>Share Current Bank Search</h3>
            <p>Copy this URL to share the current bank search with others:</p>
            <input
              type="text"
              :value="shareUrl"
              readonly
              style="width: 100%; margin: 10px 0; padding: 5px"
              @click="$event.target.select()"
            />
          </div>
          <div class="confirm-buttons">
            <button @click="copyToClipboard">Copy URL</button>
            <button @click="showSharePopup = false">Close</button>
          </div>
        </div>
      </div>

      <!-- Right Column: PDF Viewer -->
      <div v-if="showLinkedDocument" class="right-panel">
        <div
          class="pdf-viewer-title"
          style="
            display: flex;
            align-items: center;
            justify-content: space-between;
          "
        >
          Document Viewer
          <input
            ref="documentSearch"
            class="document-search"
            v-model="documentSearchTerm"
            @input="searchDocuments"
            @keydown.enter.prevent="openFirstDocumentSearchResult"
            placeholder="Search documents..."
          />
          <button
            class="select-credits-button"
            :disabled="!documentSearchTerm.trim() || serverSearchLoading"
            @click="searchDocumentsServer"
            title="Search all documents (not just cached)"
          >
            <i
              :class="
                serverSearchLoading
                  ? 'fas fa-spinner fa-spin'
                  : 'fas fa-search-plus'
              "
            ></i>
          </button>
          <div style="display: flex; gap: 5px">
            <button class="select-credits-button" @click="fetchDocuments(true)">
              <i class="fas fa-refresh"></i>
            </button>
            <button class="select-credits-button" @click="checkLinkedState">
              <i class="fas fa-link"></i>
            </button>
            <button
              @click="showLinkedDocument = false"
              class="select-credits-button"
            >
              <i class="fas fa-close"></i>
            </button>
          </div>
        </div>
        <div v-if="filteredDocuments.length" class="search-results">
          <div
            v-for="(doc, index) in filteredDocuments.slice(0, 10)"
            :key="index"
            class="search-result"
            @click="selectDocument(doc)"
          >
            <div>
              <span v-html="highlightTerm(doc.title)"></span> -
              <span v-html="highlightTerm(doc.description)"></span> -
              <span>{{ new Date(doc.date).toLocaleDateString('en-US') }}</span>
            </div>
          </div>
        </div>
        <div class="pdf-container">
          <div v-if="pdfLoading" class="pdf-loading-overlay">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
          <div v-if="pdfUrl" class="pdf-viewer" ref="pdfViewer">
            <iframe
              id="pdf-frame"
              :src="pdfUrl"
              width="750px"
              height="845px"
              frameborder="0"
              style="width: 100%; height: 100%"
              @load="onPdfFrameLoad"
            ></iframe>
          </div>
        </div>
      </div>

      <QuickbooksPage
        @rowSelected="handleQBImportSelect"
        @update-show-split-details="handleShowSplitDetails"
        @selectedLedgerRows="handleSelectedLedgerRows"
        @requestHandleMatch="handleMatch"
        @update-date-range="handleQBDateRangeUpdate"
        :selectedBankTransaction="selectedBankTransaction || {}"
        :searchValue="searchValue"
        :ledgerFromDateProp="ledgerFromDate"
        :ledgerToDateProp="ledgerToDate"
      />
      <ShortcutDialog
        :visible="showShortcuts"
        @close="toggleShortcutsDialog(false)"
      />
      <FileUploadDialog
        :visible="showFileUploadDialog"
        @close="toggleFileUploadDialog(false)"
        @success="fileUploadSuccess"
      />
      <div>
        <ChartPopup
          :visible="isChartVisible"
          :startDate="chartStartDate"
          :endDate="chartEndDate"
          @update:visible="hideChart"
          @dateSelected="updateSearchfromChart"
        />
      </div>
      <!-- Note Dialog -->
      <note-dialog
        :visible="noteDialogVisible"
        :transaction="selectedBankTransaction || {}"
        @close="noteDialogVisible = false"
        @save="saveNote"
      ></note-dialog>
    </div>
  </div>
</template>

<script>
import ShortcutDialog from './ShortcutDialog.vue';
import FileUploadDialog from './FileUploadDialog.vue';
import debounce from 'lodash/debounce';
import QuickbooksPage from './QuickbooksPage.vue'; // Adjust the path if necessary
import ChartPopup from './ChartPopup.vue';
import NoteDialog from './NoteDialog.vue';
import { ref } from 'vue';
import axios from '@/axiosConfig';
import eventBus from './eventBus.js';
import io from 'socket.io-client';
import { isDocumentViewerHotkey } from '@/utils/documentViewerHotkey';

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** True if field equals token, or is a comma-separated list containing an exact token. */
function docFieldExact(field, token) {
  if (!field || !token) return false;
  return String(field)
    .toLowerCase()
    .split(',')
    .map((part) => part.trim())
    .some((part) => part === token);
}

export default {
  components: {
    ShortcutDialog,
    FileUploadDialog,
    QuickbooksPage,
    ChartPopup,
    NoteDialog,
  },
  setup() {
    const isChartVisible = ref(false);
    const chartEndDate = ref(new Date().toISOString().split('T')[0]);
    const chartStartDate = ref(calculateStartDate(chartEndDate.value, 60));

    function calculateStartDate(endDate, daysBefore) {
      const end = new Date(endDate);
      const start = new Date(end);
      start.setDate(end.getDate() - daysBefore);
      return start.toISOString().split('T')[0];
    }

    const showChart = () => {
      isChartVisible.value = true;
    };

    const hideChart = () => {
      isChartVisible.value = false;
    };

    return {
      isChartVisible,
      chartStartDate,
      chartEndDate,
      showChart,
      hideChart,
    };
  },
  data() {
    // calculate the date selectors for the bank ledger
    let todaysDate = new Date().toISOString().substr(0, 10); // Today's date in YYYY-MM-DD format
    let lookbackDate = new Date();
    lookbackDate.setMonth(lookbackDate.getMonth() - 1); // Move to the previous month
    lookbackDate.setDate(1); // Set the day to the first day of that month
    lookbackDate = lookbackDate.toISOString().substr(0, 10); // Format as YYYY-MM-DD
    return {
      clickTimeout: null,
      suppressRightColumns: false,
      noWrapText: false,
      isPrinterFriendly: false,
      footerMessage: '',
      footerMessageTimeout: null,
      noteDialogVisible: false,
      selectedYear:
        new Date().getMonth() < 2
          ? new Date().getFullYear() - 1
          : new Date().getFullYear(),
      monthShortcuts: [
        { short: 'J', name: 'January' },
        { short: 'F', name: 'February' },
        { short: 'M', name: 'March' },
        { short: 'A', name: 'April' },
        { short: 'M', name: 'May' },
        { short: 'J', name: 'June' },
        { short: 'J', name: 'July' },
        { short: 'A', name: 'August' },
        { short: 'S', name: 'September' },
        { short: 'O', name: 'October' },
        { short: 'N', name: 'November' },
        { short: 'D', name: 'December' },
      ],

      hovered: false,
      hoveredTable: false, // Track if mouse is over shadow-box-main specifically
      selectedLedgerRows: [],
      showFileUploadDialog: false,
      showShortcuts: false,
      selectedBankTransaction: null,
      searchValue: '',
      noteDialog: false,
      selectedQBImport: null,
      showSplitDetails: false, // Local state to manage visibility
      selectedRowIds: [],
      searchQuery: '',
      rawSearchQuery: '',
      transactions: [],
      verifyFailureReasons: {}, // Map of transaction IDs to verify failure reasons
      showVerifyTooltip: false, // Show the verify tooltip
      verifyTooltipTimeout: null, // Timeout for hiding the tooltip
      earliestDate: '',
      totalRows: 0,
      ledgerToDate: todaysDate,
      ledgerFromDate: lookbackDate,
      headers: [
        'N',
        'Ledger',
        'Bank',
        'Type',
        'Bank Check',
        'Bank Ref',
        'Bank Description',
        'Credit',
        'Debit',
        'Bal',
        'Type',
        'Check No.',
        'Description',
        'Account',
        'Class',
        'Memo',
      ],
      searchHelp: false,
      filteringUnmatched: false,
      pdfUrl: '',
      pdfLoading: false,
      accountOptions: [],
      selectedAccount: 10000,
      selectedAccountName: null,
      documents: [],
      selectedDocId: null,
      // Doc id the user chose in the viewer (search/click); do not replace
      // unless they explicitly select a different ledger-linked document.
      userPinnedDocId: null,
      documentSearchTerm: '',
      filteredDocuments: [],
      serverSearchLoading: false,
      selectedLedgerRow: null,
      showLinkedDocument: true,
      showConfirm: false,
      confirmMessage: '',
      showInfoPopup: false,
      infoPopupMessage: '',
      sortByDescription: false,
      sortByAccount: false,
      showSharePopup: false,
      shareUrl: '',
      showAutomatchPopup: false,
      automatchPopupMessage: '',
      automatchPopupTimeout: null,
      automatchRunning: false,
      showDepositGroupMatchPopup: false,
      depositGroupMatchInput: '',
      depositGroupMatchError: '',
      depositGroupMatchLoading: false,
      depositGroupMatchTransaction: null,
    };
  },

  computed: {
    parsedEarliestDate() {
      return this.earliestDate ? new Date(this.earliestDate) : new Date();
    },

    filteredTransactions() {
      // Start with all transactions
      let filteredData = this.transactions;

      // First apply filter for unmatched transactions if toggled
      if (this.filteringUnmatched) {
        filteredData = filteredData.filter(
          (transaction) => transaction.matched !== 1,
        );
      }

      // If no search query, skip search filtering but still apply sorting
      if (!this.searchQuery) {
        // No additional filtering needed
      } else {
        // Split the search query by comma and trim whitespace
        const queries = this.searchQuery
          .split(',')
          .map((query) => query.trim());
        let invertSearch = false;

        queries.forEach((query, index) => {
          // Clear verify failure reasons if not using :verify
          if (index === 0 && !query.includes(':verify')) {
            this.verifyFailureReasons = {};
          }

          invertSearch = false;
          if (query.startsWith('!')) {
            invertSearch = true;
            query = query.substring(1).trim();
          }
          if (query.startsWith(':u')) {
            filteredData = filteredData.filter(
              (transaction) => transaction.matched !== 1,
            );
          } else if (query.startsWith(':n')) {
            filteredData = filteredData.filter(
              (transaction) => transaction.note != null,
            );
          } else if (query.startsWith(':verify')) {
            // Clear previous verify failure reasons
            this.verifyFailureReasons = {};

            filteredData = filteredData.filter((transaction) => {
              const condition1 =
                transaction.Account === '10000 : General Checking' &&
                (transaction.IsSplitParent === 0 ||
                  transaction.IsSplitParent === '0');

              const condition2 =
                (transaction.matched === 1 || transaction.matched === '1') &&
                transaction.IsSplitParent !== 1 &&
                transaction.IsSplitParent !== '1' &&
                (transaction.Class === null ||
                  transaction.Class === '' ||
                  transaction.Class === undefined);

              const condition3 =
                transaction.Account === null ||
                transaction.Account === '' ||
                transaction.Account === undefined ||
                (typeof transaction.Account === 'string' &&
                  transaction.Account.trim() === '');

              // Track the failure reason
              if (condition1) {
                this.verifyFailureReasons[transaction.Id] =
                  'Swapped account and split\nedit transaction and use swap button';
              } else if (condition2) {
                this.verifyFailureReasons[transaction.Id] =
                  'Missing class on matched transaction\ndouble-click to find in ledger,\nhover over ledger, then\npress "e" to edit and add correct class';
              } else if (condition3) {
                this.verifyFailureReasons[transaction.Id] =
                  'unmatched transaction. Select here, press f-key,\nthen select in ledger and press "m" key.\nIf not found or not matchable, \npress the "a" key or \nuse add to ledger button';
              }

              return condition1 || condition2 || condition3;
            });
          } else if (query.startsWith(':')) {
            const id = parseInt(query.slice(1), 10);
            filteredData = filteredData.filter(
              (transaction) => transaction.Id === id,
            );
          } else {
            if (invertSearch) {
              filteredData = filteredData.filter(
                (transaction) =>
                  !Object.values(transaction).some(
                    (value) =>
                      value != null &&
                      value
                        .toString()
                        .toLowerCase()
                        .includes(query.toLowerCase()),
                  ),
              );
            } else {
              filteredData = filteredData.filter((transaction) =>
                Object.values(transaction).some(
                  (value) =>
                    value != null &&
                    value
                      .toString()
                      .toLowerCase()
                      .includes(query.toLowerCase()),
                ),
              );
            }
          }
        });
      }

      // Apply sorting if enabled
      if (this.sortByDescription) {
        filteredData = filteredData.slice().sort((a, b) => {
          const descA = (a.BankRefDesc || '').toLowerCase();
          const descB = (b.BankRefDesc || '').toLowerCase();
          return descA.localeCompare(descB);
        });
      } else if (this.sortByAccount) {
        filteredData = filteredData.slice().sort((a, b) => {
          const accountA = (a.Account || '').toLowerCase();
          const accountB = (b.Account || '').toLowerCase();
          return accountA.localeCompare(accountB);
        });
      }

      return filteredData;
    },

    visibleTransactionCount() {
      return this.filteredTransactions.length;
    },
    selectedRowTotals() {
      let totalCredits = 0;
      let totalDebits = 0;

      this.selectedRowIds.forEach((id) => {
        const transaction = this.transactions.find((t) => t.Id === id);
        if (transaction) {
          if (transaction.Credit) {
            totalCredits += parseFloat(transaction.Credit);
          }
          if (transaction.Debit) {
            totalDebits += parseFloat(transaction.Debit);
          }
        }
      });

      return {
        count: this.selectedRowIds.length,
        totalCredits: totalCredits.toFixed(2),
        totalDebits: totalDebits.toFixed(2),
      };
    },
  },

  methods: {
    hasDepositGroup(value) {
      if (!value) return false;
      return /depositGroup\s+[\d.]+/i.test(value);
    },

    handleMemoDoubleClick(value) {
      if (!value) return;

      let searchValue = null;

      // Check for "depositGroup NNNN" pattern
      const depositMatch = value.match(/depositGroup\s+([\d.]+)/i);
      if (depositMatch) {
        searchValue = depositMatch[1];
      }

      // Check for "ACH fundingID ACH-XXXX-XXXXX" pattern - use last 8 chars
      const achMatch = value.match(/ACH\s+fundingID\s+(\S+)/i);
      if (achMatch) {
        const fullId = achMatch[1];
        searchValue = fullId.slice(-8); // Last 8 characters to match PaymentViewer display
      }

      if (searchValue) {
        // Open in a new tab
        const url = `/properties?tab=payments&search=${encodeURIComponent(searchValue)}`;
        window.open(url, '_blank');
      }
    },

    handleCloseDocumentViewer() {
      console.log(
        '📄 [MAINPAGE] Received closeDocumentViewer event, closing document viewer',
      );
      this.pdfUrl = '';
      this.pdfLoading = false;
      this.showLinkedDocument = false;
      this.userPinnedDocId = null;
    },

    /** `d` on /main: hide email and the other tool panels, show this viewer. */
    openOnlyDocumentViewer() {
      eventBus.emit('closeNavBarPanels');
      const wasClosed = !this.showLinkedDocument;
      this.showLinkedDocument = true;
      if (wasClosed && !this.pdfUrl) {
        this.fetchDocument();
      }
    },

    /** Re-load the user-pinned document if something else overwrote the viewer. */
    preserveViewerDocument() {
      if (this.userPinnedDocId == null) return;
      const pinned = Number(this.userPinnedDocId);
      const current = Number(
        this.selectedDocId || localStorage.getItem('selectedDocument'),
      );
      if (pinned && pinned !== current) {
        localStorage.setItem('selectedDocument', String(pinned));
        this.selectedDocId = pinned;
        this.fetchDocument();
      }
    },

    shareCurrentSearch() {
      const params = new URLSearchParams();

      // Add page parameter to indicate this is a bank search
      params.append('page', 'bank');

      if (this.searchQuery) {
        params.append('q', this.searchQuery);
      }
      if (this.ledgerFromDate) {
        params.append('start', this.ledgerFromDate);
      }
      if (this.ledgerToDate) {
        params.append('end', this.ledgerToDate);
      }
      if (this.sortByDescription) {
        params.append('sortDesc', 'true');
      }
      if (this.sortByAccount) {
        params.append('sortAccount', 'true');
      }
      if (this.selectedAccountName) {
        params.append('account', this.selectedAccountName);
      }

      // Use current page URL as base, removing any existing query parameters
      const currentUrl = new URL(window.location.href);
      currentUrl.search = ''; // Clear existing query parameters
      const baseUrl = currentUrl.toString();

      this.shareUrl = `${baseUrl}?${params.toString()}`;
      this.showSharePopup = true;
    },
    handleSearchInputKeydown(event) {
      // Check if down arrow key is pressed
      if (event.key === 'ArrowDown') {
        const value = this.rawSearchQuery.trim();

        // Check if the value is a number
        if (value && !isNaN(value)) {
          event.preventDefault(); // Prevent cursor movement

          const numValue = parseFloat(value);
          // Decrement the value
          const newValue = numValue - 1;

          // Update the search query with the decremented value
          // Preserve decimal places if original had them
          if (value.includes('.')) {
            const decimalPlaces = value.split('.')[1]?.length || 0;
            this.rawSearchQuery = newValue.toFixed(decimalPlaces);
          } else {
            this.rawSearchQuery = newValue.toString();
          }
        }
      }
      // Check if up arrow key is pressed (optional: increment)
      else if (event.key === 'ArrowUp') {
        const value = this.rawSearchQuery.trim();

        // Check if the value is a number
        if (value && !isNaN(value)) {
          event.preventDefault(); // Prevent cursor movement

          const numValue = parseFloat(value);
          // Increment the value
          const newValue = numValue + 1;

          // Update the search query with the incremented value
          // Preserve decimal places if original had them
          if (value.includes('.')) {
            const decimalPlaces = value.split('.')[1]?.length || 0;
            this.rawSearchQuery = newValue.toFixed(decimalPlaces);
          } else {
            this.rawSearchQuery = newValue.toString();
          }
        }
      }
    },

    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        // Provide feedback that the URL was copied
        const originalText = event.target.textContent;
        event.target.textContent = 'Copied!';
        setTimeout(() => {
          event.target.textContent = originalText;
        }, 1000);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = this.shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    },

    toggleDescriptionSort() {
      this.sortByDescription = !this.sortByDescription;
      // Turn off account sort when description sort is toggled
      if (this.sortByDescription) {
        this.sortByAccount = false;
      }
      // Force Vue to re-evaluate computed properties
      this.$forceUpdate();
    },

    toggleAccountSort() {
      this.sortByAccount = !this.sortByAccount;
      // Turn off description sort when account sort is toggled
      if (this.sortByAccount) {
        this.sortByDescription = false;
      }
      // Force Vue to re-evaluate computed properties
      this.$forceUpdate();
    },

    async fetchDocuments(force = false) {
      let latestDocId;
      try {
        if (!force && this.documents?.length > 0) {
          const latestDocResponse = await axios.get('/api/get-latest-docid');
          latestDocId = latestDocResponse.data.id;
        }

        if (
          force ||
          !this.documents?.length ||
          !latestDocId ||
          this.documents[0]?.id < latestDocId
        ) {
          // Calculate date range: ledgerFromDate - 30 days to ledgerToDate + 30 days
          const fromDate = new Date(this.ledgerFromDate);
          fromDate.setDate(fromDate.getDate() - 30);
          const fromDateStr = fromDate.toISOString().split('T')[0];

          const toDate = new Date(this.ledgerToDate);
          toDate.setDate(toDate.getDate() + 30);
          const toDateStr = toDate.toISOString().split('T')[0];

          const updatedResponse = await axios.get('/api/get-documents', {
            params: {
              fromDate: fromDateStr,
              toDate: toDateStr,
            },
          });
          this.documents = updatedResponse.data;
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    },

    handleAccountChange() {
      const selectedAccount = this.accountOptions.find(
        (account) => account.accountNumber === this.selectedAccount,
      );
      if (selectedAccount) {
        this.selectedAccountName = selectedAccount.accountName;
        this.fetchTransactions();
      }
    },

    async fetchAccounts() {
      try {
        const response = await axios.get('api/get-bank-accounts', {});
        this.accountOptions = response.data;

        // Check if account was set from URL params
        const urlParams = new URLSearchParams(window.location.search);
        let accountParam = urlParams.get('account');

        if (accountParam) {
          // Decode + signs to spaces (URLSearchParams doesn't handle this automatically)
          accountParam = accountParam.replace(/\+/g, ' ');

          // Find and set the account based on URL param
          const account = this.accountOptions.find(
            (acc) => acc.accountName === accountParam,
          );
          if (account) {
            this.selectedAccount = account.accountNumber;
            this.selectedAccountName = account.accountName;
          } else if (this.accountOptions.length > 0) {
            // Fallback to first account if URL param account not found
            this.selectedAccount = this.accountOptions[0].accountNumber;
            this.selectedAccountName = this.accountOptions[0].accountName;
          }
        } else if (this.accountOptions.length > 0) {
          // Default to first account if no URL param
          this.selectedAccount = this.accountOptions[0].accountNumber;
          this.selectedAccountName = this.accountOptions[0].accountName;
        }
      } catch (error) {
        this.logMessages += 'Failed to load accounts.\n';
        console.error('Error fetching accounts:', error);
      }
    },

    usePattern(pattern) {
      this.searchQuery = pattern;
      this.searchHelp = false;
    },

    printerFriendly() {
      this.isPrinterFriendly = !this.isPrinterFriendly;
      this.noWrapText = !this.noWrapText;
      this.suppressRightColumns = !this.suppressRightColumns;
    },

    print() {
      const table = document.getElementById('printableTable');
      let printContent = table.outerHTML;

      // Check if we need to suppress columns to the right of 'Balance'
      if (this.suppressRightColumns) {
        // Clone the table element to manipulate it without affecting the DOM
        const tableClone = table.cloneNode(true);

        // Remove columns to the right of 'Balance' from the clone
        tableClone.querySelectorAll('tr').forEach((row) => {
          row
            .querySelectorAll('td:nth-child(n+11), th:nth-child(n+11)')
            .forEach((cell) => {
              cell.remove();
            });
        });

        // Update printContent with the modified table
        printContent = tableClone.outerHTML;
      }

      const printWindow = window.open('', '_blank');
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Table</title>
            <style>
              /* Set landscape orientation */
              @page {
                size: landscape;
              }

              /* General table styles for printing */
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.8em;
              }
              table, th, td {
                border: 1px solid #888;
              }
              th, td {
                padding: 8px;
                text-align: left;
              }
              .even {
                background-color: #c8c6c6; /* Darker gray for even rows */
              }
              .odd {
                background-color: #ffffff; /* White for odd rows */
              }
              /* Force browsers to print background colors */
              @media print {
                body {
                  -webkit-print-color-adjust: exact; /* Chrome, Safari */
                  print-color-adjust: exact; /* Firefox */
                }
              }
            </style>
          </head>
         <body onload="window.print(); window.close();">
            ${printContent}
          </body>
        </html>
      `);
      printWindow.document.close();
    },

    toggleSuppressRightColumns() {
      this.suppressRightColumns = !this.suppressRightColumns; // Toggle suppression
    },

    applyFilter() {
      if (!this.searchQuery) {
        this.filteredTransactionsData = this.transactions;
        return;
      }

      // Split the search query by comma and trim whitespace
      const queries = this.searchQuery.split(',').map((query) => query.trim());
      let invertSearch = false;

      let filteredData = this.transactions;

      queries.forEach((query) => {
        invertSearch = false;
        if (query.startsWith('!')) {
          invertSearch = true;
          query = query.substring(1).trim();
        }
        if (query.startsWith(':u')) {
          this.displayFooterMessage('Filtering for unmatched transactions');
          filteredData = filteredData.filter(
            (transaction) => transaction.matched !== 1,
          );
        } else if (query.startsWith(':n')) {
          this.displayFooterMessage('Filtering for transactions with notes');
          filteredData = filteredData.filter(
            (transaction) => transaction.note != null,
          );
        } else if (query.star(':')) {
          this.displayFooterMessage(
            'Filtering for specific row number from ledger (ctrl-b)',
          );
          const id = parseInt(this.searchQuery.slice(1), 10);
          filteredData = filteredData.filter(
            (transaction) => transaction.Id === id,
          );
        } else {
          if (invertSearch) {
            this.displayFooterMessage('Inverting filter');
            filteredData = filteredData.filter(
              (transaction) =>
                !Object.values(transaction).some(
                  (value) =>
                    value != null &&
                    value
                      .toString()
                      .toLowerCase()
                      .includes(query.toLowerCase()),
                ),
            );
          } else {
            filteredData = filteredData.filter((transaction) =>
              Object.values(transaction).some(
                (value) =>
                  value != null &&
                  value.toString().toLowerCase().includes(query.toLowerCase()),
              ),
            );
          }
        }
      });

      this.filteredTransactionsData =
        filteredData.length > 0 ? filteredData : [];
    },

    displayFooterMessage(message) {
      this.footerMessage = message;

      // Clear any existing timeouts
      if (this.footerMessageTimeout) {
        clearTimeout(this.footerMessageTimeout);
      }

      // Set a timeout to clear the message after 3 seconds
      this.footerMessageTimeout = setTimeout(() => {
        // Apply fading effect
        const footerMessageDiv = document.querySelector('.footer-message');
        if (footerMessageDiv) {
          footerMessageDiv.classList.add('fade-out');
          setTimeout(() => {
            this.footerMessage = '';
            footerMessageDiv.classList.remove('fade-out');
          }, 1000); // The duration of the fade-out effect
        }
      }, 3000);
    },

    openNoteDialog(transaction) {
      this.selectedBankTransaction = transaction;
      this.noteDialogVisible = true;
    },

    async saveNote(updatedTransaction) {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.post(
          '/api/addnote/bank',
          {
            id: updatedTransaction.Id,
            note: updatedTransaction.note,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.success) {
          // Update the transaction list with the new note
          this.selectedBankTransaction.note = updatedTransaction.note;
          this.noteDialogVisible = false;
        } else {
          // Handle error
          console.error('Failed to save note');
        }
      } catch (error) {
        console.error('Error:', error);
      }
    },

    updateSearchfromChart(selectedDate) {
      const dateParts = selectedDate.split('-');
      const year = dateParts[0] - 2000;
      const month = dateParts[1].padStart(2, '0');
      const day = dateParts[2].padStart(2, '0');
      const formattedDate = `${month}/${day}/${year}`;
      this.searchQuery = formattedDate;
      //console.log('searchquery:', this.searchQuery);
    },

    handleSelectedLedgerRows(selectedRows) {
      this.selectedLedgerRows = selectedRows || [];
      // Keep the single "active" row in sync with the live selection objects
      // from QuickbooksPage (avoids stale linkedDocId after table refreshes).
      // Do NOT open linked documents here — only user-driven ledgerRowSelected
      // with openLinkedDocument:true should change the document viewer (so a
      // doc the user searched for is not replaced when a new ledger row is saved).
      if (this.selectedLedgerRows.length === 1) {
        this.selectedLedgerRow = this.selectedLedgerRows[0];
      } else if (this.selectedLedgerRows.length === 0) {
        // Visual selection cleared — do not keep a stale row for link checks
        this.selectedLedgerRow = null;
      }
    },

    fileUploadSuccess() {
      this.showFileUploadDialog = false;
      //console.log('upload succeeded refreshing')
      this.fetchTransactions();
    },

    toggleShortcutsDialog(show = true) {
      this.showShortcuts = show;
    },

    toggleFileUploadDialog(show = true) {
      this.showFileUploadDialog = show;
    },

    handleQBImportSelect(row) {
      this.selectedQBImport = row;
    },

    handleBankTransactionSelect(row) {
      this.selectedBankTransaction = row;
    },

    async handleMatch() {
      // Ensure at least one row in QBImport and one row in bankTransaction are selected
      if (!this.selectedBankTransaction) {
        this.displayFooterMessage(
          'No bank transaction selected. Select a bank transaction first.',
        );
        return;
      }
      if (this.selectedLedgerRows.length === 0) {
        this.displayFooterMessage(
          'No ledger row selected. Select a ledger row first.',
        );
        return;
      }

      const bankTransaction = this.selectedBankTransaction;
      const qbImport = this.selectedLedgerRows;

      // Single selection case
      if (qbImport.length === 1) {
        const singleQbImport = qbImport[0];
        // console.log('single selected row')
        // console.log('qbImport: ', singleQbImport)

        // Check if the amounts match
        if (
          Math.abs(bankTransaction.Credit) ===
            Math.abs(singleQbImport.Amount) ||
          Math.abs(bankTransaction.Debit) === Math.abs(singleQbImport.Amount)
        ) {
          // console.log('amounts match');
          // Preserve Memo if it contains 'depositGroup'
          const preserveMemo =
            bankTransaction.Memo &&
            /depositGroup\s+[\d.]+/i.test(bankTransaction.Memo);

          const updatedFields = {
            datetime: new Date(singleQbImport.Date)
              .toISOString()
              .slice(0, 19)
              .replace('T', ' '),
            checkNumber: singleQbImport.CheckNum || '',
            Type: singleQbImport.Type || '',
            Description: singleQbImport.Name || '',
            Account: singleQbImport.Account || '',
            Class: singleQbImport.Class || '',
            Memo: preserveMemo
              ? bankTransaction.Memo
              : singleQbImport.Memo || '',
            BankRefNum: bankTransaction.BankRefNum,
            linkedLedgerId: singleQbImport.Id,
            IsSplitParent: singleQbImport.IsSplitParent || 0,
          };

          try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
              '/api/matchTransactions',
              {
                bankTransactionId: bankTransaction.Id,
                qbImportId: singleQbImport.Id,
                updatedFields,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );
            if (response.data.message === 'Transactions matched successfully') {
              // Update properties directly (Vue 3 reactivity handles this)
              this.selectedBankTransaction.ledgerDate = singleQbImport.Date;
              this.selectedBankTransaction.checkNumber = singleQbImport.Num;
              this.selectedBankTransaction.Type = singleQbImport.Type;
              this.selectedBankTransaction.Description = singleQbImport.Name;
              this.selectedBankTransaction.Account = singleQbImport.Account;
              this.selectedBankTransaction.Class = singleQbImport.Class;
              // Only update Memo if it doesn't contain depositGroup
              if (!preserveMemo) {
                this.selectedBankTransaction.Memo = singleQbImport.Memo;
              }
              this.selectedBankTransaction.linkedLedgerId = singleQbImport.Id;
              this.selectedBankTransaction.IsSplitParent =
                singleQbImport.IsSplitParent || 0;
              this.selectedBankTransaction.matched = 1;

              // Update the QBImport Matched status to 1
              singleQbImport.Matched = 1;

              // If search query in QuickbooksPage is set to filter for unmatched items, change it
              // This will be handled in the event handler in QuickbooksPage
              eventBus.emit('matchSuccessful', singleQbImport.Id);

              this.displayFooterMessage(
                `Matched bank transaction ${bankTransaction.Id} with ledger ${singleQbImport.Id}`,
              );
            }
          } catch (error) {
            console.error(error);
            this.displayFooterMessage(
              'Error matching transactions: ' + error.message,
            );
          }
        } else {
          this.displayFooterMessage(
            `Amounts don't match. Bank: ${bankTransaction.Credit || bankTransaction.Debit}, Ledger: ${singleQbImport.Amount}`,
          );
        }
      } else if (qbImport.length > 1) {
        // Multiple selection case
        // console.log('multiple selected rows');
        try {
          await this.handleMultipleMatch(bankTransaction, qbImport);
        } catch (error) {
          console.error(error);
        }
      }
      this.fetchTransactions();
    },

    async handleMultipleMatch(bankTransaction, qbImportRows) {
      const scaleFactor = 100;
      const totalAmount =
        qbImportRows.reduce(
          (sum, row) => sum + Math.round(Math.abs(row.Amount) * scaleFactor),
          0,
        ) / scaleFactor;

      if (
        Math.abs(bankTransaction.Credit) === totalAmount ||
        Math.abs(bankTransaction.Debit) === totalAmount
      ) {
        // console.log('amounts match for multiple rows');
        const firstRow = qbImportRows[0];

        const updatedFields = {
          Description: '-SPLIT-',
          datetime: new Date(firstRow.Date)
            .toISOString()
            .slice(0, 19)
            .replace('T', ' '),
          Account: firstRow.Account || '',
        };

        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(
            '/api/matchMultipleTransactions',
            {
              bankTransactionId: bankTransaction.Id,
              qbImportRows: qbImportRows.map((row) => row.Id),
              updatedFields,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (response.data.message === 'Transactions matched successfully') {
            this.selectedBankTransaction.Description = '-SPLIT-';
            this.selectedBankTransaction.ledgerDate = firstRow.Date;
            this.selectedBankTransaction.Account = firstRow.Account;
            qbImportRows.forEach(
              (row) => (
                (row.Matched = 1),
                (row.LinkedTransactionId = this.selectedBankTransaction.Id)
              ),
            );
          }
        } catch (error) {
          console.error(error);
        }
      }
    },

    handleShowSplitDetails(show) {
      this.showSplitDetails = show;
    },

    selectCredits() {
      // Get current date in local timezone
      const now = new Date();

      // Calculate payment date ranges based on the day of month
      const currentDay = now.getDate();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      let currentPeriodStart;

      // If today is the 25th or later in the month, current cycle starts on 25th of current month
      // Otherwise current cycle starts on 25th of previous month
      if (currentDay >= 25) {
        // Current cycle: 25th of current month to now
        currentPeriodStart = new Date(currentYear, currentMonth, 25);
      } else {
        // Current cycle: 25th of previous month to now
        currentPeriodStart = new Date(currentYear, currentMonth - 1, 25);
      }

      // Always use the cycle start date when today is before the 25th
      // This ensures we're always using the appropriate billing cycle
      let effectiveStartDate = currentPeriodStart;

      // Format the date as YYYY-MM-DD for ledgerFromDate
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Set the from date filter to the calculated start date
      this.ledgerFromDate = formatDate(effectiveStartDate);

      this.selectedRowIds = []; // Clear previous selections

      // Display the date range being used
      this.displayFooterMessage(
        `Selecting credits from ${effectiveStartDate.toLocaleDateString()} to ${now.toLocaleDateString()}`,
      );

      for (let i = 0; i < this.transactions.length; i++) {
        const row = this.transactions[i];
        const rowDate = new Date(row.BankRefDateTime);

        // Check if the transaction is within our date range and is a credit
        if (rowDate >= effectiveStartDate && rowDate <= now && row.Credit > 0) {
          const bankType = String(row.BankType || row.Type || '').toUpperCase();
          const acctNum = parseInt(String(row.Account || '').trim(), 10);
          const skipTrxf =
            bankType === 'TRXF' && (acctNum === 10300 || acctNum === 10410);
          if (!skipTrxf) {
            this.selectedRowIds.push(row.Id);
          }
        } else if (rowDate < effectiveStartDate) {
          // Stop iteration once we go beyond the target date
          // This assumes transactions are sorted by date descending
          break;
        }
      }

      // Scroll the table to the first selected row
      if (this.selectedRowIds.length > 0) {
        this.$nextTick(() => {
          const firstSelectedRowId = this.selectedRowIds[0];
          const firstSelectedRowElement = document.querySelector(
            `#row-${firstSelectedRowId}`,
          );
          if (firstSelectedRowElement) {
            firstSelectedRowElement.classList.add('selected-row-scroll');
            firstSelectedRowElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        });
      } else {
        this.displayFooterMessage(
          'No credit transactions found in the selected date range',
        );
      }
    },

    stripId(transaction) {
      const temp = { ...transaction };
      delete temp.Id;
      delete temp.linkedToBank;
      delete temp.matched;
      delete temp.note;
      delete temp.IsSplitParent;
      delete temp.linkedLedgerId;
      delete temp.SplitRef;
      delete temp.ledgerMatched;
      return temp;
    },

    async fetchTransactionInfo() {
      try {
        const response = await axios.get('/api/transactions/info', {
          params: {
            account: this.selectedAccount,
          },
        });
        this.earliestDate = response.data[0].earliestDate;
        this.totalRows = response.data[0].totalRows;
        // this.date1 = new Date(this.earliestDate).toISOString().split('T')[0]
        // this.date2 = new Date().toISOString().split('T')[0]
      } catch (error) {
        console.error('Error fetching transaction info:', error);
      }
    },

    async fetchTransactions() {
      try {
        const response = await axios.get('/api/transactions', {
          params: {
            date1: this.ledgerFromDate,
            date2: this.ledgerToDate,
            account: this.selectedAccount,
          },
        });
        this.transactions = response.data;
        this.totalRows = response.data.length;
        // force the filter to refresh
        const temp = this.searchQuery;
        this.searchQuery = '';
        this.searchQuery = temp;
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push('/login');
        } else {
          console.error('Error fetching transactions:', error);
        }
      }
    },

    clearSearch() {
      this.rawSearchQuery = '';
      this.searchQuery = '';
    },

    clearSelection() {
      this.selectedRowIds = [];
      // Remove the 'selected-row' class from all rows
      const selectedRows = document.querySelectorAll('.selected-row-scroll');
      selectedRows.forEach((row) =>
        row.classList.remove('selected-row-scroll'),
      );
    },

    highlightSearch(value) {
      if (!this.searchQuery) return value;

      // Only highlight if results < 30
      if (this.filteredTransactions.length >= 30) return value;

      // Split search query by comma and get the last token
      const queries = this.searchQuery.split(',').map((query) => query.trim());
      const lastQuery = queries[queries.length - 1];

      // Only highlight if search term length > 2
      if (!lastQuery || lastQuery.length <= 2) return value;

      // Skip highlighting for special commands that start with :
      if (lastQuery.startsWith(':')) return value;

      const escapedQuery = escapeRegExp(lastQuery);
      const searchRegex = new RegExp(`(${escapedQuery})`, 'gi');
      return value != null
        ? value
            .toString()
            .replace(searchRegex, '<span class="highlight">$1</span>')
        : value;
    },

    handleRowClick(transaction, event) {
      const transactionId = transaction.Id;
      this.selectedBankTransaction = transaction;
      //console.log('transaction: ', transaction)
      if (event.ctrlKey || event.metaKey) {
        this.toggleSelection(transactionId);
      } else if (event.shiftKey) {
        if (transactionId) this.selectRange(transactionId);
      } else {
        this.singleSelect(transactionId);
      }
    },

    toggleSelection(transactionId) {
      const index = this.selectedRowIds.indexOf(transactionId);
      if (index > -1) {
        this.selectedRowIds.splice(index, 1);
      } else {
        this.selectedRowIds.push(transactionId);
      }
    },

    selectRange(transactionId) {
      if (this.selectedRowIds.length === 0) {
        this.selectedRowIds.push(transactionId);
      } else {
        const lastSelectedId =
          this.selectedRowIds[this.selectedRowIds.length - 1];
        const startIndex = this.filteredTransactions.findIndex(
          (transaction) => transaction.Id === lastSelectedId,
        );
        const endIndex = this.filteredTransactions.findIndex(
          (transaction) => transaction.Id === transactionId,
        );

        const [start, end] =
          startIndex < endIndex
            ? [startIndex, endIndex]
            : [endIndex, startIndex];
        for (let i = start; i <= end; i++) {
          const id = this.filteredTransactions[i].Id;
          if (!this.selectedRowIds.includes(id)) {
            this.selectedRowIds.push(id);
          }
        }
      }
    },

    singleSelect(transactionId) {
      eventBus.emit('bankTransactionSelected', transactionId);
      if (
        this.selectedRowIds.length === 1 &&
        this.selectedRowIds[0] === transactionId
      ) {
        this.selectedRowIds = [];
      } else {
        this.selectedRowIds = [transactionId];
      }
    },

    isSelected(transactionId) {
      return this.selectedRowIds.includes(transactionId);
    },

    formatNumber(value) {
      if (typeof value !== 'number') value = parseFloat(value);
      if (isNaN(value)) return '';
      if (value === 0.0) return '';
      const sign = value < 0 ? '- ' : '';
      return `${sign}${Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    handleDateRangeChange(dates) {
      this.ledgerFromDate = dates[0];
      this.ledgerToDate = dates[1];
      this.fetchTransactions();
    },

    handleKeyDown(event) {
      if (isDocumentViewerHotkey(event)) {
        event.preventDefault();
        event.stopPropagation();
        this.openOnlyDocumentViewer();
        return;
      }

      // Helper function to check if an input field has focus
      const isInputFocused = () => {
        const activeElement = document.activeElement;
        return (
          activeElement &&
          (activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.contentEditable === 'true')
        );
      };

      // Skip key handling if note dialog is visible
      if (this.noteDialogVisible) {
        return;
      }

      if (this.showDepositGroupMatchPopup) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeDepositGroupMatchPopup();
        }
        return;
      }

      // Global shortcut: "$" opens Cash Position regardless of hover state
      if (event.key === '$' && !isInputFocused()) {
        event.preventDefault();
        eventBus.emit('openCashPosition');
        return;
      }

      // Only respond to keyboard events when the mouse is over the shadow-box-main element
      if (!this.hoveredTable) return;

      // If the Escape key is pressed
      if (event.key === 'Escape') {
        // Always clear selected rows and unmatched filter
        this.selectedRowIds = [];
        this.filteringUnmatched = false;

        // if there is no query, refresh
        if (this.searchQuery === '') {
          this.rawSearchQuery = '';
          this.footerMessage = 'refreshing ...';
          this.fetchTransactions();
          return;
        }
        if (this.selectedLedgerRows.length === 0) {
          // If there are no selected payments, clear the search query
          this.searchQuery = '';
        } else {
          // Otherwise, clear the selected payments
          this.selectedLedgerRows = [];
        }
      }

      if (event.key === 'f') {
        // If we're in an input field, allow normal typing behavior
        if (isInputFocused()) {
          return;
        }

        // Otherwise prevent default and handle the 'f' key functionality
        event.preventDefault();

        // Check if mouse is hovering over a td in the 'Bank Check' column
        const hoveredElement = document.querySelector('td:hover');
        const headerIndex = this.headers.indexOf('Bank Check');

        if (hoveredElement) {
          const cellIndex = Array.from(
            hoveredElement.parentElement.children,
          ).indexOf(hoveredElement);

          // If we're hovering over a cell in the Bank Check column (adjust for the Note column)
          if (cellIndex === headerIndex && this.selectedBankTransaction) {
            // +1 because we have a note column as first column
            // Get the check number value and strip leading zeros
            let checkNumber = hoveredElement.textContent.trim();
            checkNumber = checkNumber.replace(/^0+/, ''); // Strip leading zeros

            if (checkNumber) {
              this.searchValue = checkNumber;
              this.displayFooterMessage(
                `Searching for check number: ${checkNumber}`,
              );
              return;
            }
          }
        }

        // Enhanced 'f' key functionality for bank transactions:
        // If we're not hovering over a Bank Check cell or it's empty, use the improved search logic
        // Check if selectedBankTransaction exists and has the required properties
        if (
          this.selectedBankTransaction &&
          (this.selectedBankTransaction.Debit !== undefined ||
            this.selectedBankTransaction.Credit !== undefined)
        ) {
          // Priority 1: If the transaction has a linkedLedgerId, use precise ID-based search
          // This will show the exact linked ledger entry and any related split transactions
          if (this.selectedBankTransaction.linkedLedgerId) {
            const searchString = `:id=${this.selectedBankTransaction.linkedLedgerId}`;
            this.searchValue = searchString;
            this.displayFooterMessage(
              `Searching for linked ledger entry ID: ${this.selectedBankTransaction.linkedLedgerId}`,
            );
            return;
          }

          // Priority 2: Fall back to amount-based search
          // Calculate the value to be passed with proper null checks
          const debitValue =
            this.selectedBankTransaction.Debit !== undefined
              ? parseFloat(this.selectedBankTransaction.Debit || 0)
              : 0;
          const creditValue =
            this.selectedBankTransaction.Credit !== undefined
              ? parseFloat(this.selectedBankTransaction.Credit || 0)
              : 0;

          const value = Math.abs(
            (isNaN(debitValue) ? 0 : debitValue) +
              (isNaN(creditValue) ? 0 : creditValue),
          ).toString();

          // Set searchValue to search the ledger
          const searchString = ':!m,' + 1.0 * value;
          this.searchValue = searchString;

          // Also show a footer message to confirm the action
          this.displayFooterMessage('Searching for matching amount in ledger');
        } else {
          this.displayFooterMessage(
            'Please select a valid bank transaction first',
          );
        }
      }

      if (event.key === 'u') {
        // If we're in an input field, allow normal typing behavior
        if (isInputFocused()) {
          return;
        }

        // Otherwise prevent default and handle the 'u' key functionality
        event.preventDefault();
        this.findUnmatched();
      }

      if (
        event.shiftKey &&
        (event.key === 'A' || event.key === 'a') &&
        !isInputFocused()
      ) {
        event.preventDefault();
        this.triggerAutomatchOnly();
        return;
      }

      if (
        event.shiftKey &&
        (event.key === 'M' || event.key === 'm') &&
        !event.ctrlKey &&
        !event.metaKey &&
        !isInputFocused()
      ) {
        event.preventDefault();
        this.openDepositGroupMatchPopup();
        return;
      }

      // Shift+I: create split children under selected ledger parent from selected bank rows
      if (
        event.shiftKey &&
        (event.key === 'I' || event.key === 'i') &&
        !event.ctrlKey &&
        !event.metaKey &&
        !isInputFocused()
      ) {
        event.preventDefault();
        this.createSplitChildrenFromSelection();
        return;
      }

      if (event.key === 'a') {
        // If we're in an input field, allow normal typing behavior
        if (isInputFocused()) {
          return;
        }

        // Otherwise prevent default and handle the 'a' key functionality
        event.preventDefault();

        // Check if exactly one row is selected
        if (this.selectedRowIds.length === 1) {
          this.addToLedger();
          this.displayFooterMessage('Adding selected transaction to ledger');
        } else if (this.selectedRowIds.length === 0) {
          this.displayFooterMessage('Please select a transaction first');
        } else {
          this.displayFooterMessage('Please select exactly one transaction');
        }
      }

      if (event.key === '?') {
        // If we're in an input field, allow normal typing behavior
        if (isInputFocused()) {
          return;
        }

        // Otherwise prevent default and handle the '?' key functionality
        event.preventDefault();
        this.searchHelp = !this.searchHelp;
        this.displayFooterMessage(
          this.searchHelp ? 'Showing search help' : 'Hiding search help',
        );
      }

      if (event.ctrlKey && event.key === 'm') {
        this.handleMatch(); // if a QB row is selected and CTRL-m is pressed...
      } else if (
        event.ctrlKey &&
        event.key == 'b' &&
        (!this.selectedQBImport ||
          !this.selectedQBImport.LinkedTransactionId ||
          !this.selectedQBImport.Matched)
      ) {
        this.displayFooterMessage(
          'Must select an already selected, matched transaction in ledger. hint: use :m',
        );
      } else if (
        event.ctrlKey &&
        event.key == 'b' &&
        this.selectedQBImport?.LinkedTransactionId
      ) {
        this.displayFooterMessage(
          'Filtering for matching transaction from ledger',
        );
        this.searchQuery = ':' + this.selectedQBImport.LinkedTransactionId;
      } else if (event.ctrlKey && event.key == 'F' && this.selectedQBImport) {
        this.searchQuery = this.selectedQBImport.Amount;
      } else if (event.ctrlKey && event.key === 'ArrowDown') {
        this.scrollToBottom();
      } else if (event.ctrlKey && event.key === 'ArrowUp') {
        this.scrollToTop();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        // Only handle arrow keys if not in an input field
        const activeElement = document.activeElement;
        const isInputField =
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable;

        if (isInputField) {
          return;
        }

        event.preventDefault();

        // Find current account index
        const currentIndex = this.accountOptions.findIndex(
          (account) => account.accountNumber === this.selectedAccount,
        );

        if (currentIndex !== -1) {
          let newIndex;
          if (event.key === 'ArrowRight') {
            // Move to next account (or wrap to first)
            newIndex = (currentIndex + 1) % this.accountOptions.length;
          } else {
            // Move to previous account (or wrap to last)
            newIndex =
              currentIndex === 0
                ? this.accountOptions.length - 1
                : currentIndex - 1;
          }

          // Update selected account
          this.selectedAccount = this.accountOptions[newIndex].accountNumber;
          this.handleAccountChange();

          this.displayFooterMessage(
            `Switched to account: ${this.accountOptions[newIndex].accountNumber} : ${this.accountOptions[newIndex].accountName}`,
          );
        }
      }
    },

    scrollToBottom() {
      const tableContainer = this.$refs.tableContainer;
      if (tableContainer) {
        tableContainer.scrollTop = tableContainer.scrollHeight;
      }
    },

    scrollToTop() {
      const tableContainer = this.$refs.tableContainer;
      if (tableContainer) {
        tableContainer.scrollTop = 0;
      }
    },

    // For edit dialog
    handleMouseEnter() {
      this.hovered = true;
    },

    handleMouseLeave() {
      this.hovered = false;
    },

    handleTableEnter() {
      this.hoveredTable = true;
      this.footerMessage = 'bank ledger active';
    },

    handleTableLeave() {
      this.hoveredTable = false;
      this.footerMessage = 'bank ledger inactive';
    },

    clearAutomatchPopupTimeout() {
      if (this.automatchPopupTimeout) {
        clearTimeout(this.automatchPopupTimeout);
        this.automatchPopupTimeout = null;
      }
    },

    openDepositGroupMatchPopup() {
      if (this.selectedRowIds.length !== 1) {
        this.displayFooterMessage(
          'Select exactly one bank transaction to match a deposit group',
        );
        return;
      }

      const transactionId = this.selectedRowIds[0];
      const transaction =
        this.filteredTransactions.find((t) => t.Id === transactionId) ||
        this.selectedBankTransaction;

      if (!transaction) {
        this.displayFooterMessage('Selected transaction not found');
        return;
      }

      if (transaction.matched === 1) {
        this.displayFooterMessage('This transaction is already matched');
        return;
      }

      const credit = parseFloat(transaction.Credit || 0);
      if (!credit || credit <= 0) {
        this.displayFooterMessage(
          'Deposit group matching requires a positive Credit amount',
        );
        return;
      }

      this.depositGroupMatchTransaction = transaction;
      this.depositGroupMatchInput = '';
      this.depositGroupMatchError = '';
      this.showDepositGroupMatchPopup = true;

      this.$nextTick(() => {
        this.$refs.depositGroupMatchInput?.focus();
      });
    },

    closeDepositGroupMatchPopup() {
      if (this.depositGroupMatchLoading) {
        return;
      }
      this.showDepositGroupMatchPopup = false;
      this.depositGroupMatchInput = '';
      this.depositGroupMatchError = '';
      this.depositGroupMatchTransaction = null;
    },

    async submitDepositGroupMatch() {
      const depositGroup = this.depositGroupMatchInput.trim();
      if (!depositGroup) {
        this.depositGroupMatchError = 'Enter a deposit group (e.g. 071626.1)';
        return;
      }

      if (!this.depositGroupMatchTransaction) {
        this.depositGroupMatchError = 'No transaction selected';
        return;
      }

      this.depositGroupMatchLoading = true;
      this.depositGroupMatchError = '';

      try {
        const response = await axios.post('/api/match-deposit-group', {
          bankTransactionId: this.depositGroupMatchTransaction.Id,
          depositGroup,
        });

        const matchedGroup = response.data.depositGroup || depositGroup;
        const transaction = this.depositGroupMatchTransaction;
        transaction.FundingId = matchedGroup;
        transaction.Type = 'DEP';
        transaction.Account = '40000 : Rents Collected';
        transaction.checkNumber = 'DEP';
        transaction.Memo = `depositGroup ${matchedGroup}`;
        transaction.matched = 1;

        this.depositGroupMatchLoading = false;
        this.closeDepositGroupMatchPopup();
        this.displayFooterMessage(
          `Matched to depositGroup ${matchedGroup} (${response.data.paymentCount || 0} payment(s))`,
        );
        this.fetchTransactions();
      } catch (error) {
        this.depositGroupMatchError =
          error.response?.data?.error || 'Failed to match deposit group';
        this.depositGroupMatchLoading = false;
      }
    },

    triggerAutomatchOnly() {
      if (this.automatchRunning) return;
      this.automatchRunning = true;
      this.clearAutomatchPopupTimeout();
      this.showAutomatchPopup = true;
      this.automatchPopupMessage = 'Running Automatch...';
      eventBus.emit('run-automatch-only');
    },

    handleAutomatchOnlyComplete(result) {
      this.automatchRunning = false;
      this.automatchPopupMessage =
        result?.message ||
        (result?.success
          ? `Automatch completed. ${result?.matched || 0} transactions matched.`
          : 'Automatch failed.');
      this.clearAutomatchPopupTimeout();
      this.automatchPopupTimeout = setTimeout(() => {
        this.showAutomatchPopup = false;
        this.automatchPopupMessage = '';
        this.automatchPopupTimeout = null;
      }, 2000);
    },

    findUnmatched() {
      this.filteringUnmatched = !this.filteringUnmatched;
      // Clear the search input when toggling unmatched filter
      this.rawSearchQuery = '';
      this.searchQuery = '';
      // Reset date range to year-to-date when toggling unmatched filter
      this.resetDateRange('ytd');
    },

    handleDblClick(transaction) {
      // Clear the click timeout to prevent the single-click handler from firing
      if (this.clickTimeout) {
        clearTimeout(this.clickTimeout);
        this.clickTimeout = null; // Reset the timeout variable
      }

      // Check if the ledger date is earlier than the current from date and adjust if needed
      if (transaction.ledgerDate && this.ledgerFromDate) {
        // Convert MM/DD/YY format to YYYY-MM-DD for comparison
        const ledgerDateFormatted = this.parseDateShort(transaction.ledgerDate);

        if (ledgerDateFormatted) {
          const ledgerDate = new Date(ledgerDateFormatted);
          const fromDate = new Date(this.ledgerFromDate);

          if (ledgerDate < fromDate) {
            this.ledgerFromDate = ledgerDateFormatted;
          }
        }
      }

      // Check if the ledger date is later than the current to date and adjust if needed
      if (transaction.ledgerDate && this.ledgerToDate) {
        // Convert MM/DD/YY format to YYYY-MM-DD for comparison
        const ledgerDateFormatted = this.parseDateShort(transaction.ledgerDate);

        if (ledgerDateFormatted) {
          const ledgerDate = new Date(ledgerDateFormatted);
          const toDate = new Date(this.ledgerToDate);

          if (ledgerDate > toDate) {
            this.ledgerToDate = ledgerDateFormatted;
          }
        }
      }

      // Check if this is a split parent transaction with a linked ledger ID
      if (transaction.IsSplitParent === 1 && transaction.linkedLedgerId) {
        // Use ID-based filtering for split parent transactions
        this.searchValue = `:id=${transaction.linkedLedgerId}`;
        this.toggleSelection(transaction.Id);
        return;
      }

      // Check if this is a split child transaction (has SplitRef pointing to parent)
      if (transaction.SplitRef) {
        // Use ID-based filtering for the parent transaction, but also highlight the specific child
        const highlightChild = transaction.linkedLedgerId
          ? `,highlight=${transaction.linkedLedgerId}`
          : '';
        this.searchValue = `:id=${transaction.SplitRef}${highlightChild}`;
        this.toggleSelection(transaction.Id);
        return;
      }

      // Default behavior for non-split transactions
      // If this is a matched transaction (has linkedLedgerId), search for that ledger entry
      if (transaction.linkedLedgerId) {
        this.searchValue = `:id=${transaction.linkedLedgerId}`;
        this.toggleSelection(transaction.Id);
        return;
      }

      // For unmatched transactions, search by amount
      // Calculate the value to be passed
      const value = Math.abs(
        (isNaN(parseFloat(this.selectedBankTransaction.Debit))
          ? 0
          : parseFloat(this.selectedBankTransaction.Debit)) +
          (isNaN(parseFloat(this.selectedBankTransaction.Credit))
            ? 0
            : parseFloat(this.selectedBankTransaction.Credit)),
      ).toString();

      // Emit the custom event with the calculated value
      this.searchValue = ':!m,' + 1.0 * value;
      this.toggleSelection(transaction.Id);
    },

    // Set search to verify mode
    setVerifySearch() {
      this.searchQuery = ':verify';
      this.rawSearchQuery = ':verify';
      // Also set the date range to current year like the YT button
      this.resetDateRange('ytd');

      // Show the verify tooltip
      this.showVerifyTooltip = true;

      // Clear any existing timeout
      if (this.verifyTooltipTimeout) {
        clearTimeout(this.verifyTooltipTimeout);
      }

      // Hide the tooltip after 10 seconds
      this.verifyTooltipTimeout = setTimeout(() => {
        this.showVerifyTooltip = false;
      }, 10000);
    },

    // Format date from YYYY-MM-DD to MM/DD/YY
    formatDateShort(dateString) {
      if (!dateString) return '';

      // Parse the date string manually to avoid timezone issues
      const parts = dateString.split('-');
      if (parts.length !== 3) return '';

      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]);
      const day = parseInt(parts[2]);

      if (isNaN(year) || isNaN(month) || isNaN(day)) return '';

      const monthStr = String(month).padStart(2, '0');
      const dayStr = String(day).padStart(2, '0');
      const yearStr = String(year).slice(-2);

      return `${monthStr}/${dayStr}/${yearStr}`;
    },

    // Parse MM/DD/YY or MM/DD/YYYY to YYYY-MM-DD
    parseDateShort(dateString) {
      if (!dateString) return '';

      const parts = dateString.split('/');
      if (parts.length !== 3) return '';

      let [month, day, year] = parts;

      // Convert 2-digit year to 4-digit
      if (year.length === 2) {
        const currentYear = new Date().getFullYear();
        const currentCentury = Math.floor(currentYear / 100) * 100;
        year = currentCentury + parseInt(year);

        // If the resulting year is more than 50 years in the future, assume previous century
        if (year > currentYear + 50) {
          year -= 100;
        }
      }

      // Pad month and day with zeros if needed
      month = month.padStart(2, '0');
      day = day.padStart(2, '0');

      return `${year}-${month}-${day}`;
    },

    // Update from date
    updateFromDate(event) {
      const inputValue = event.target.value;
      this.tempFromDate = inputValue;
    },

    // Update to date
    updateToDate(event) {
      const inputValue = event.target.value;
      this.tempToDate = inputValue;
    },

    // Validate and set from date
    validateFromDate(event) {
      const inputValue = event.target.value;
      const parsedDate = this.parseDateShort(inputValue);
      if (parsedDate) {
        this.ledgerFromDate = parsedDate;
      }
    },

    // Validate and set to date
    validateToDate(event) {
      const inputValue = event.target.value;
      const parsedDate = this.parseDateShort(inputValue);
      if (parsedDate) {
        this.ledgerToDate = parsedDate;
      }
    },

    // Export transactions to Excel
    async exportTransactions() {
      try {
        // Check if there are transactions to export
        if (
          !this.filteredTransactions ||
          this.filteredTransactions.length === 0
        ) {
          alert('No transactions to export. Please adjust your filters.');
          return;
        }

        const response = await axios.post(
          '/api/export-transactions',
          { transactions: this.filteredTransactions },
          { responseType: 'blob' },
        );

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'transactions.xlsx'; // Default filename
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch && filenameMatch.length === 2)
            filename = filenameMatch[1];
        }

        // Create a URL for the file and trigger a download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename); // Set dynamic filename
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url); // Clean up the URL object
      } catch (error) {
        console.error('Error exporting transactions:', error);
        alert('Failed to export transactions. Please try again.');
      }
    },

    debouncedUpdateSearchQuery: debounce(function (newVal) {
      this.searchQuery = newVal;
    }, 200),
    resetDateRange(option) {
      const today = new Date();

      if (option === 'ytd') {
        // Use string-based date construction to avoid timezone issues
        const currentYear = today.getFullYear();
        const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
        const currentDay = String(today.getDate()).padStart(2, '0');

        // Set start date to January 1st of current year
        this.ledgerFromDate = `${currentYear}-01-01`;
        // Set end date to today
        this.ledgerToDate = `${currentYear}-${currentMonth}-${currentDay}`;
      } else {
        // Default behavior - 90 days or start of year to end of month
        const endOfMonth = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0,
        );
        const ninetyDaysAgo = new Date(today);
        ninetyDaysAgo.setDate(today.getDate() - 90);
        const startOfYear = new Date(today.getFullYear(), 0, 1);

        const fromDate =
          ninetyDaysAgo > startOfYear ? ninetyDaysAgo : startOfYear;
        const toDate = endOfMonth;

        // Format the dates to 'YYYY-MM-DD' format
        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        this.ledgerFromDate = formatDate(fromDate);
        this.ledgerToDate = formatDate(toDate);
      }

      this.fetchTransactions();
    },

    setYearRange(year) {
      this.selectedYear = year;
      this.ledgerFromDate = `${year}-01-01`;
      this.ledgerToDate = `${year}-12-31`;
      this.fetchTransactions();
      this.displayFooterMessage(`Date range set to ${year}`);
    },

    setMonthRange(monthIndex) {
      const year = this.selectedYear;

      // Create start date (first day of selected month)
      const startDate = new Date(year, monthIndex, 1);

      // Create end date (last day of selected month)
      const endDate = new Date(year, monthIndex + 1, 0);

      // Format dates to YYYY-MM-DD
      const formatDate = (date) => {
        const y = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${y}-${month}-${day}`;
      };

      this.ledgerFromDate = formatDate(startDate);
      this.ledgerToDate = formatDate(endDate);

      // Show feedback message
      const monthName = this.monthShortcuts[monthIndex].name;
      this.displayFooterMessage(`Date range set to ${monthName} ${year}`);

      this.fetchTransactions();
    },

    expandSearch() {
      const currentYear = new Date().getFullYear();

      // First day of the current year
      let fromDate = new Date(currentYear, 0, 1);

      // Check if fromDate is already the first day of the current year
      if (this.ledgerFromDate === fromDate.toISOString().split('T')[0]) {
        // Set fromDate to 365 days earlier
        fromDate.setDate(fromDate.getDate() - 365);
      }

      // Last day of the current year
      const toDate = new Date(currentYear, 11, 31);

      // Format the dates to 'YYYY-MM-DD' format
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      this.ledgerFromDate = formatDate(fromDate);
      this.ledgerToDate = formatDate(toDate);
      this.fetchTransactions();
    },

    focusSearch() {
      if (this.$refs.searchQuery) this.$refs.searchQuery.focus();
      this.$nextTick(() => {
        this.$refs.searchQuery.select();
      });
    },

    addToLedger() {
      console.log(
        'attempting insert New Ledger Row:',
        this.selectedBankTransaction,
      );
      eventBus.emit('insertNewLedgerRow', this.selectedBankTransaction);
    },

    /**
     * Unmatched mode (broken-link filter on): selected bank rows become split
     * children of the single selected ledger parent. Opens the ledger edit dialog.
     */
    createSplitChildrenFromSelection() {
      if (!this.filteringUnmatched) {
        this.displayFooterMessage(
          'Turn on the unmatched filter first, then select bank rows and a ledger parent',
        );
        return;
      }

      if (this.selectedRowIds.length === 0) {
        this.displayFooterMessage(
          'Select one or more bank transactions to add as split children',
        );
        return;
      }

      const parent =
        Array.isArray(this.selectedLedgerRows) &&
        this.selectedLedgerRows.length === 1
          ? this.selectedLedgerRows[0]
          : null;

      if (!parent?.Id) {
        this.infoPopupMessage =
          'You must select exactly one row below as the parent.';
        this.showInfoPopup = true;
        return;
      }

      const bankTransactions = this.selectedRowIds
        .map((id) => this.transactions.find((t) => t.Id === id))
        .filter(Boolean);

      if (bankTransactions.length === 0) {
        this.displayFooterMessage('Could not resolve selected bank rows');
        return;
      }

      eventBus.emit('createSplitChildrenFromBankRows', {
        parent,
        bankTransactions,
      });
    },

    async createSecurityDepositLedger() {
      // Check if a single row is selected
      if (!this.selectedBankTransaction) {
        this.displayFooterMessage('Please select a single transaction');
        return;
      }

      const transaction = this.selectedBankTransaction;

      // Check if the selected account dropdown is Security Deposit Account (10300)
      if (this.selectedAccount !== 10300) {
        this.displayFooterMessage(
          'This feature only works when Security Deposit Account (10300) is selected',
        );
        return;
      }

      // Check if Memo contains a depositGroup string
      const memo = transaction.Memo || '';
      const depositGroupMatch = memo.match(/depositGroup\s+(\S+)/i);
      if (!depositGroupMatch) {
        this.displayFooterMessage('No depositGroup found in the Memo field');
        return;
      }

      const depositGroup = depositGroupMatch[1];

      // Check if already linked to a ledger ID
      if (transaction.linkedLedgerId) {
        this.displayFooterMessage(
          `This transaction is already linked to ledger ID ${transaction.linkedLedgerId}`,
        );
        return;
      }

      try {
        const response = await axios.post(
          '/api/create-security-deposit-ledger',
          {
            bankTransactionId: transaction.Id,
            depositGroup: depositGroup,
            depositDate: transaction.BankRefDateTime,
            totalAmount: parseFloat(transaction.Credit || 0),
          },
        );

        if (response.data.parentLedgerId) {
          // Update local transaction data
          transaction.linkedLedgerId = response.data.parentLedgerId;

          this.displayFooterMessage(
            `Security deposit ledger created (${response.data.paymentCount} payment(s)) - Ledger ID: ${response.data.parentLedgerId}`,
          );
        }
      } catch (error) {
        console.error('Error creating security deposit ledger:', error);
        this.displayFooterMessage(
          error.response?.data?.error ||
            'Failed to create security deposit ledger entry',
        );
      }
    },

    onPdfFrameLoad() {
      this.pdfLoading = false;
    },

    async fetchDocument() {
      const id = parseInt(localStorage.getItem('selectedDocument'));
      if (id) {
        this.selectedDocId = id;
        if (this.pdfUrl && String(this.pdfUrl).startsWith('blob:')) {
          URL.revokeObjectURL(this.pdfUrl);
        }
        this.pdfUrl = '';
        this.pdfLoading = true;
        try {
          // Fetch the PDF directly from the backend
          const response = await axios.get(`/api/load-pdf/${id}`, {
            responseType: 'arraybuffer', // Important for binary data like PDFs
          });

          if (response.status === 200) {
            // Create a Blob from the response data
            const pdfBlob = new Blob([response.data], {
              type: 'application/pdf',
            });
            const pdfUrl = URL.createObjectURL(pdfBlob);

            // Set the URL for your iframe or download link
            this.pdfUrl = pdfUrl;
            this.displayFooterMessage(`PDF for document ID ${id} loaded.`);
          } else {
            this.pdfLoading = false;
            console.error(
              `Failed to load PDF for document ID ${id}. Status: ${response.status}`,
            );
            this.displayFooterMessage('Failed to load PDF: Server error.');
          }
        } catch (error) {
          this.pdfLoading = false;
          console.error('Error loading PDF:', error);
          this.displayFooterMessage('Failed to load PDF.');
        }
      }
    },

    searchDocuments() {
      const term = this.documentSearchTerm.toLowerCase();
      if (!term) {
        this.filteredDocuments = [];
        return;
      }

      const tokens = term
        .split(',')
        .map((token) => token.trim())
        .filter(Boolean);

      this.filteredDocuments = this.documents.filter((doc) =>
        tokens.every((token) =>
          ['title', 'note', 'description'].some(
            (key) => doc[key] && doc[key].toLowerCase().includes(token),
          ),
        ),
      );

      // Auto-open only on exact field match (all tokens). Substring uniqueness
      // alone is too aggressive — typing "16" for "16.94" can uniquely match
      // another doc, clear the box via selectDocument, and block further input.
      if (this.filteredDocuments.length === 1 && tokens.length > 0) {
        const only = this.filteredDocuments[0];
        const exact = tokens.every((token) =>
          ['title', 'note', 'description'].some(
            (key) => docFieldExact(only[key], token),
          ),
        );
        if (exact) {
          this.selectDocument(only);
          this.$nextTick(() => {
            const input = this.$refs.documentSearch;
            if (input && typeof input.blur === 'function') {
              input.blur();
            }
          });
        }
      }
    },

    /** Enter: open the first filtered result (or unique list item). */
    openFirstDocumentSearchResult() {
      if (!this.filteredDocuments?.length) {
        this.searchDocuments();
      }
      if (this.filteredDocuments?.length) {
        this.selectDocument(this.filteredDocuments[0]);
        this.$nextTick(() => {
          const input = this.$refs.documentSearch;
          if (input && typeof input.blur === 'function') {
            input.blur();
          }
        });
      }
    },

    async searchDocumentsServer() {
      const term = this.documentSearchTerm.trim();
      if (!term) return;
      this.serverSearchLoading = true;
      try {
        const response = await axios.get('/api/search-documents', {
          params: { q: term },
        });
        this.filteredDocuments = response.data;
      } catch (error) {
        console.error('Error searching documents:', error);
        this.displayFooterMessage('Failed to search documents');
      } finally {
        this.serverSearchLoading = false;
      }
    },

    highlightTerm(text) {
      if (!text) return '';
      const term = this.documentSearchTerm;
      const regex = new RegExp(`(${escapeRegExp(term)})`, 'gi');
      // if text contains commas, split it into separate parts
      const parts = text.split(',').map((part) => part.trim());
      return parts
        .map((part) =>
          part.replace(regex, '<span class="highlight-doc">$1</span>'),
        )
        .join(', ');
    },

    selectDocument(doc) {
      this.selectedDocId = doc.id;
      this.userPinnedDocId = doc.id;
      this.documentSearchTerm = '';
      this.filteredDocuments = [];
      localStorage.setItem('selectedDocument', doc.id);
      this.fetchDocument();
    },

    confirmAction() {
      this.showConfirm = false;
      this.linkDocToLedger();
    },

    /** Prefer the live single-row selection from QB; fall back to eventBus row. */
    resolveSelectedLedgerRow() {
      if (
        Array.isArray(this.selectedLedgerRows) &&
        this.selectedLedgerRows.length === 1
      ) {
        return this.selectedLedgerRows[0];
      }
      return this.selectedLedgerRow;
    },

    async linkDocToLedger() {
      const docId = this.selectedDocId;
      const row = this.resolveSelectedLedgerRow();
      this.selectedLedgerRow = row;

      if (!row?.Id) {
        this.displayFooterMessage('Select a ledger row first');
        return;
      }

      if (docId) {
        await axios
          .post('/api/set-ledger-documentid', {
            documentId: docId,
            ledgerId: row.Id,
          })
          .then((response) => {
            if (
              response.data.message ===
              'Ledger document ID updated successfully'
            ) {
              row.linkedDocId = docId;
              if (this.selectedLedgerRow) {
                this.selectedLedgerRow.linkedDocId = docId;
              }
              if (
                this.selectedLedgerRows?.length === 1 &&
                this.selectedLedgerRows[0]?.Id === row.Id
              ) {
                this.selectedLedgerRows[0].linkedDocId = docId;
              }
              this.displayFooterMessage(
                'Document linked to ledger successfully',
              );
            } else {
              this.displayFooterMessage('Failed to link document to ledger');
            }
          })
          .catch((error) => {
            console.error('Error linking document to ledger:', error);
            this.displayFooterMessage('Failed to link document to ledger');
          });
      }
    },

    /**
     * Payload may be a raw ledger row (legacy) or
     * { row, openLinkedDocument } from QuickbooksPage.
     * Default openLinkedDocument is false unless explicitly true — avoids
     * clobbering a document the user searched for when a row is selected
     * after save/socket/refresh.
     */
    handleLedgerRowSelected(payload) {
      let row = payload;
      let openLinkedDocument = false;
      if (
        payload &&
        typeof payload === 'object' &&
        Object.prototype.hasOwnProperty.call(payload, 'row') &&
        Object.prototype.hasOwnProperty.call(payload, 'openLinkedDocument')
      ) {
        row = payload.row;
        openLinkedDocument = payload.openLinkedDocument === true;
      } else if (payload && payload.Id != null) {
        // Legacy raw-row emit: treat as user selection only if no pin
        openLinkedDocument = this.userPinnedDocId == null;
      }

      console.log('eventBus: Main: from ledger: selected row:', row, {
        openLinkedDocument,
      });
      if (!row) {
        this.selectedLedgerRow = null;
        return;
      }
      const prevId = this.selectedLedgerRow?.Id;
      this.selectedLedgerRow = row;
      // Keep multi-select array aligned when eventBus is the source of truth
      if (
        !this.selectedLedgerRows?.length ||
        this.selectedLedgerRows.length === 1
      ) {
        this.selectedLedgerRows = [row];
      }
      // Only open a linked document on intentional user selection of a row
      // (click / arrows). Programmatic selection never sets openLinkedDocument.
      if (
        openLinkedDocument &&
        row.linkedDocId &&
        Number(row.Id) !== Number(prevId)
      ) {
        // User chose a ledger row with a linked doc — pin follows that doc
        this.userPinnedDocId = null;
        localStorage.setItem('selectedDocument', row.linkedDocId);
        console.log(
          '📄 [MAINPAGE] Emitting closeNavBarPanels - opening document viewer',
        );
        eventBus.emit('closeNavBarPanels');
        this.showLinkedDocument = true;
        this.fetchDocument();
      }
    },

    handleBankStatementUpdated() {
      this.displayFooterMessage('Bank statement updated. Refreshing...');
      this.fetchTransactions();
    },

    handleBankTransactionUpdated(bankTransactionId) {
      console.log('Bank transaction updated event received:', {
        bankTransactionId,
        timestamp: new Date().toISOString(),
      });
      this.displayFooterMessage(
        `Bank transaction ${bankTransactionId} updated. Refreshing...`,
      );
      // Refresh the bank transactions to show the updated data
      this.fetchTransactions();
    },

    updateSearchFromEventBus(query) {
      this.searchQuery = query;
      this.rawSearchQuery = query;
      this.displayFooterMessage(`Search query updated to: ${query}`);
      this.resetDateRange('ytd');
    },

    handleQBDateRangeUpdate({ from, to, selectedYear }) {
      this.ledgerFromDate = from;
      this.ledgerToDate = to;
      if (selectedYear) this.selectedYear = selectedYear;
      this.fetchTransactions();
    },

    handleSetMonthRange(monthIndex) {
      // Call the existing setMonthRange method
      this.setMonthRange(monthIndex);
    },

    handleSetSearchInput(searchString) {
      this.rawSearchQuery = searchString;
      this.searchQuery = searchString;
    },

    // Function to connect to WebSocket server
    connectSocket() {
      try {
        const socketUrl = process.env.VUE_APP_SOCKET_URL;

        if (!socketUrl) {
          console.error('Socket URL is undefined.');
          return;
        }

        const token = this.getAuthToken();

        this.socket = io(socketUrl, {
          query: {
            token: token,
            source: 'docuScan',
          },
        });

        this.socket.on('connect', () => {
          console.log('page: Connected to WebSocket server');
        });

        /*
        this.socket.onAny((event, ...args) => {
          console.log(`Received event: ${event}`, args);
        });
        */

        this.socket.on('transactions-changed', () => {
          this.fetchTransactions();
        });

        this.socket.on('ledgerDataChanged', () => {});

        this.socket.on('ledgerDataDeleted', () => {});

        this.socket.on('documentUpdated', () => {
          this.fetchDocuments();
        });

        this.socket.on('disconnect', () => {
          console.log('Disconnected from WebSocket server');
        });
      } catch (error) {
        console.error('Error connecting to WebSocket server:', error);
      }
    },

    getAuthToken() {
      return localStorage.getItem('token');
    },

    checkLinkedState() {
      const row = this.resolveSelectedLedgerRow();
      this.selectedLedgerRow = row;
      if (!row?.Id) {
        this.displayFooterMessage('Select a ledger row first');
        return;
      }
      if (row.linkedDocId) {
        this.confirmMessage =
          'this ledger entry already has a linked document.<br><br>Click OK to replace';
        this.showConfirm = true;
      } else {
        this.linkDocToLedger();
      }
    },

    /** Shift+L from QuickbooksPage: same as document viewer link icon. */
    handleLinkDocumentHotkey() {
      if (!this.showLinkedDocument) {
        this.displayFooterMessage(
          'Open the document viewer with a document first',
        );
        return;
      }
      if (!this.selectedDocId) {
        this.displayFooterMessage('No document is open in the viewer');
        return;
      }
      this.checkLinkedState();
    },
  },

  created() {
    document.title = 'Ledgers';
  },

  beforeUnmount() {
    // Clean up the event listeners when the component is destroyed
    document.removeEventListener('keydown', this.handleKeydown);
    eventBus.off('show-shortcuts-app', this.toggleShortcutsDialog);
    eventBus.off('upload-bankfile-app', this.toggleFileUploadDialog);
    eventBus.off('ledgerRowSelected', this.handleLedgerRowSelected);
    eventBus.off('bankStatementUpdated', this.handleBankStatementUpdated);
    eventBus.off('requestHandleMatch', this.handleMatch);
    eventBus.off('set-search-query', this.updateSearchFromEventBus);
    eventBus.off('bankTransactionUpdated', this.handleBankTransactionUpdated);
    eventBus.off('set-month-range', this.handleSetMonthRange);
    eventBus.off('set_search_input', this.handleSetSearchInput);
    eventBus.off('reset-date-range', this.resetDateRange);
    eventBus.off('closeDocumentViewer', this.handleCloseDocumentViewer);
    eventBus.off('mainPageFooterMessage', this.displayFooterMessage);
    eventBus.off('automatch-only-complete', this.handleAutomatchOnlyComplete);
    eventBus.off(
      'linkDocumentToSelectedLedger',
      this.handleLinkDocumentHotkey,
    );
    eventBus.off('preserveViewerDocument', this.preserveViewerDocument);
    window.removeEventListener('keydown', this.handleKeyDown);
    this.clearAutomatchPopupTimeout();

    // Clear verify tooltip timeout
    if (this.verifyTooltipTimeout) {
      clearTimeout(this.verifyTooltipTimeout);
    }
  },

  async mounted() {
    // Handle URL parameters for shared searches
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    const searchParam = urlParams.get('q');
    const startParam = urlParams.get('start');
    const endParam = urlParams.get('end');
    const sortDescParam = urlParams.get('sortDesc');
    const sortAccountParam = urlParams.get('sortAccount');
    const monthParam = urlParams.get('month');
    const yearParam = urlParams.get('year');

    // Handle page=banktransactions with month/year filter (from Financial Summary drilldown)
    if (pageParam === 'banktransactions' && monthParam && yearParam) {
      const monthNames = [
        'jan',
        'feb',
        'mar',
        'apr',
        'may',
        'jun',
        'jul',
        'aug',
        'sep',
        'oct',
        'nov',
        'dec',
      ];
      const monthIndex = monthNames.indexOf(monthParam.toLowerCase());
      if (monthIndex !== -1) {
        const year = parseInt(yearParam, 10);
        // Set start date to first day of month
        const startDate = new Date(year, monthIndex, 1);
        // Set end date to last day of month
        const endDate = new Date(year, monthIndex + 1, 0);
        this.ledgerFromDate = startDate.toISOString().split('T')[0];
        this.ledgerToDate = endDate.toISOString().split('T')[0];
      }
    }

    // Only process bank-related parameters if this is a bank page share or no page specified (backward compatibility)
    if (!pageParam || pageParam === 'bank') {
      if (searchParam) {
        this.searchQuery = searchParam;
        this.rawSearchQuery = searchParam;
        // If search param provided without explicit dates, set to full year (YTD behavior)
        if (!startParam && !endParam) {
          const currentYear = new Date().getFullYear();
          this.ledgerFromDate = `${currentYear}-01-01`;
          this.ledgerToDate = new Date().toISOString().split('T')[0];
        }
      }
      if (startParam) {
        this.ledgerFromDate = startParam;
      }
      if (endParam) {
        this.ledgerToDate = endParam;
      }
      if (sortDescParam === 'true') {
        this.sortByDescription = true;
      }
      if (sortAccountParam === 'true') {
        this.sortByAccount = true;
      }
    }

    // Handle page=financials parameter to auto-open Financial Summary
    if (pageParam === 'financials') {
      // Emit event to NavBar to open Financial Summary
      // We need to wait a bit to ensure NavBar is mounted and event listeners are set up
      this.$nextTick(() => {
        eventBus.emit('auto-open-financials');

        // Also try with a timeout as backup
        setTimeout(() => {
          eventBus.emit('auto-open-financials');
        }, 100);
      });
    }

    this.connectSocket();

    this.fetchDocument();
    this.fetchDocuments();
    await this.fetchAccounts();
    this.fetchTransactions();
    this.fetchTransactionInfo();
    document.addEventListener('keydown', this.handleKeyDown);
    eventBus.on('show-shortcuts-app', this.toggleShortcutsDialog);
    eventBus.on('upload-bankfile-app', this.toggleFileUploadDialog);
    eventBus.on('ledgerRowSelected', this.handleLedgerRowSelected);
    eventBus.on('bankStatementUpdated', this.handleBankStatementUpdated);
    eventBus.on('requestHandleMatch', this.handleMatch);
    eventBus.on('set-search-query', this.updateSearchFromEventBus);
    eventBus.on('bankTransactionUpdated', this.handleBankTransactionUpdated);
    eventBus.on('set-month-range', this.handleSetMonthRange);
    eventBus.on('set_search_input', this.handleSetSearchInput);
    eventBus.on('reset-date-range', this.resetDateRange);

    // Listen for closeDocumentViewer event from NavBar
    console.log(
      '📄 [MAINPAGE] Mounted - registering closeDocumentViewer listener',
    );
    eventBus.on('closeDocumentViewer', this.handleCloseDocumentViewer);
    eventBus.on('mainPageFooterMessage', this.displayFooterMessage);
    eventBus.on('automatch-only-complete', this.handleAutomatchOnlyComplete);
    eventBus.on(
      'linkDocumentToSelectedLedger',
      this.handleLinkDocumentHotkey,
    );
    eventBus.on('preserveViewerDocument', this.preserveViewerDocument);
  },

  watch: {
    rawSearchQuery: {
      handler: function (newVal) {
        // Immediate search for special patterns (commands that start with :)
        if (newVal && (newVal.startsWith(':') || newVal.startsWith('!'))) {
          this.searchQuery = newVal;
        } else {
          // Debounced search for regular text
          this.debouncedUpdateSearchQuery(newVal);
        }
      },
    },

    searchQuery(newVal) {
      // Split the search query by comma and trim whitespace
      if (newVal) {
        const queries = newVal.split(',').map((query) => query.trim());
        let invertSearch = false;
        queries.forEach((query) => {
          invertSearch = false;
          if (query.startsWith('!')) {
            invertSearch = true;
            query = query.substring(1).trim();
          }
          if (query.startsWith(':u')) {
            this.displayFooterMessage('Filtering for unmatched transactions');
          } else if (query.startsWith(':n')) {
            this.displayFooterMessage('Filtering for transactions with notes');
          } else if (query.startsWith(':')) {
            this.displayFooterMessage(
              'Filtering for specific row number from ledger (ctrl-b)',
            );
          } else {
            if (invertSearch) {
              this.displayFooterMessage('Inverting filter');
            }
          }
        });
      }
    },

    isChartVisible(newValue) {
      if (newValue) {
        this.$nextTick(() => {});
      }
    },

    footerMessage() {
      setTimeout(() => {
        this.footerMessage = '';
      }, 5000);
    },
  },
};
</script>

<style scoped>
.deposit-group-link {
  cursor: pointer;
  color: #a080a8;
}

.deposit-group-link:hover {
  text-decoration: underline;
}

.title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: -14px;
  margin-bottom: -13px;
}

.account-selection {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  margin-left: auto;
  margin-top: -5px;
}

.account-selection label {
  margin-right: 10px; /* Adjust spacing between label and dropdown */
  font-size: 0.8em;
}

.account-selection select {
  margin-right: 10px; /* Adjust spacing between dropdown and button */
  font-size: 9pt;
  padding: 1px;
  border-radius: 4px;
  border: 1pt solid #aaa;
}

.table-container {
  position: relative;
  /* overflow-y:hidden; */
  width: 1281px;
  height: 300px; /* Adjust the height as needed */
  user-select: none;
  overflow-x: auto;
  overflow-y: auto;
  scrollbar-width: none; /* For Firefox */
  background: white;
}

.table-container::-webkit-scrollbar {
  display: none; /* Hide scrollbar for WebKit-based browsers */
}

.table {
  width: 1280px;
  border-collapse: collapse;
  font-size: 11px; /* Smaller font size */
}

.table th {
  padding: 3px;
  text-align: left;
}

.table td {
  padding: 2px 2px 3px 3px;
  text-align: left;
  border-left: 1px solid #ddd; /* Light grey border */
}

.table th {
  position: sticky;
  top: 0;
  background-color: #a898b7; /* Purple header background */
  padding: 3px;
  color: white; /* White text color */
}

.table th.sortable-header {
  cursor: pointer;
  user-select: none;
}

.table th.sortable-header:hover {
  background-color: #9a87b0;
}

.table th.right-align,
.table td.right-align {
  text-align: right;
  min-width: 65px;
}
.table th.center-align,
.table td.center-align {
  text-align: center;
  min-width: 30px;
}

.table th.wide-column,
.table td.wide-column {
  max-width: 200px;
  min-width: 150px;
  overflow: hidden;
}

.table th.fixed-column,
.table td.fixed-column {
  max-width: 70px;
  white-space: nowrap; /* Prevent text wrapping */
  overflow: hidden; /* Hide overflow */
  text-overflow: ellipsis; /* Add ellipses for overflow */
}

.text-ellipsis {
  white-space: nowrap; /* Prevent text wrapping */
  overflow: hidden; /* Hide overflow */
  text-overflow: ellipsis; /* Add ellipses for overflow */
  width: 170px;
}
.text-ellipsis-narrow {
  white-space: nowrap; /* Prevent text wrapping */
  overflow: hidden; /* Hide overflow */
  text-overflow: ellipsis; /* Add ellipses for overflow */
  width: 112px;
}

.table tbody tr:nth-child(even) {
  background-color: #dddbdb; /* Light gray for even rows */
}

.table tbody tr:nth-child(odd) {
  background-color: white; /* White for odd rows */
}

.search-container {
  background-color: #dddddd;
  display: flex;
  gap: 5px;
  margin-bottom: 0px;
  max-width: 1271px;
  min-width: 1000px;
  justify-content: space-between;
  align-items: center;
  padding: 5px; /* Add some padding to ensure it doesn't touch the edges */
  padding-bottom: 8px; /* Add some padding to ensure it doesn't touch the edges */
  overflow-x: auto;
}

.search-input {
  width: 100px;
  padding: 5px;
  height: 13px;
  border-radius: 4px;
  border: 1pt solid #aaa;
  transition: border-color 0.2s ease;
}

.search-input.pending {
  border-color: #a898b7;
  box-shadow: 0 0 2px rgba(168, 152, 183, 0.4);
}

.clear-button {
  cursor: pointer;
  margin-left: 5px;
  color: white;
  background-color: #9487a1;
  margin-left: 3px;
  border-radius: 4px;
  border: 1pt solid #634f69;
  padding: 4px;
  width: 29px;
}

.row-count {
  margin-left: 10px;
  font-size: 9pt;
  padding-top: 15px;
  color: #888888;
  font-style: italic;
}
:deep(.selected-row) {
  background-color: rgb(226 203 235) !important;
}

.selected-row-totals {
  font-size: 9pt;
  font-style: italic;
  color: #888888;
  margin-top: 5px;
  margin-left: 10px;
  margin-right: 10px;
}

.totals-wrapper {
  margin-right: 0;
  margin-top: 0px;
}

:deep(.highlight) {
  background-color: #e7a4f1;
}

.from-to-label {
  margin-left: 5px;
  font-size: 9pt;
  position: relative;
}

.date-picker {
  width: 60px;
  height: 20px;
  border-radius: 4px;
  border: 1pt solid #aaa;
}

.month-shortcuts {
  position: absolute;
  top: -18px;
  left: -59px;
  display: flex;
}

.month-button {
  background-color: #e8e8e8;
  border: 1px solid #ccc;
  border-radius: 2px;
  padding: 1px 4px;
  font-size: 8px;
  color: #343232;
  cursor: pointer;
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.month-button:hover {
  background-color: #d0d0d0;
  color: #333;
}

.month-button:active {
  background-color: #b8b8b8;
}

.year-button {
  background-color: #e8e8e8;
  border: 1px solid #ccc;
  border-radius: 2px;
  padding: 1px 4px;
  font-size: 8px;
  color: #343232;
  cursor: pointer;
  width: 16px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.year-button:hover {
  background-color: #d0d0d0;
  color: #333;
}

.year-selected {
  background-color: #888;
  color: #fff;
}

.year-selected:hover {
  background-color: #666;
  color: #fff;
}

.year-month-gap {
  width: 12px;
}

.autoselect-label {
  font-size: 0.8em;
  margin-left: 10px;
  margin-right: 10px;
  font-style: italic;
  color: #888888;
}

.select-credits-button {
  color: white;
  background-color: #9487a1;
  margin-left: 3px;
  border-radius: 4px;
  border: 1pt solid #634f69;
  padding: 4px;
  width: 29px;
}

.select-credits-button-faded {
  color: white;
  background-color: #696073;
  margin-left: 3px;
  border-radius: 4px;
  border: 1pt solid #634f69;
  padding: 4px;
  width: 29px;
}

.transactions-footer {
  height: 20px;
  max-width: 1270px;
  background-color: #c4c4c4;
  padding: 5px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  text-align: left;
  display: flex;
  justify-content: left;
}

.footer-message {
  width: 99%;
  padding: 3px;
  font-size: 9pt;
  color: white;
  background: #a898b7;
  transition: opacity 1s;
  padding-left: 8px;
  padding-right: 8px;
}

.fade-out {
  opacity: 0;
}

.nothing-found {
  margin-top: 15px;
  margin-left: 15px;
  margin-bottom: 10px;
  font-size: 14pt;
  color: #c3c2c2;
}

.selected-row-scroll {
  scroll-margin-top: 30px; /* Adjust this value based on the height of your header */
}

.shadow-box-main {
  box-shadow: 9px 8px 7px 2px rgba(0, 0, 0, 0.2);
  margin-left: 30px;
  width: 1283px;
  border-radius: 4px;
}

.search-help {
  position: absolute;
  left: 150px;
  top: 150px;
  z-index: 6000;
  width: 720px;
  height: 600px;
  box-shadow: 9px 13px 7px 4px rgb(0 0 0 / 22%);
  padding: 10px;
  background: #d5d5d5;
  border-radius: 4px;
  border: 1pt solid #555;
  font-family: Arial, sans-serif;
  display: flex;
  flex-direction: column;
}

.search-help .table-wrapper {
  overflow-y: auto;
  flex: 1;
  margin-top: 10px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.search-help .table-wrapper::-webkit-scrollbar {
  display: none; /* WebKit */
}

.search-help table {
  width: 97%;
  border-collapse: collapse;
  font-size: 0.8em;
  font-family: consolas;
  margin-left: 10px;
}

.search-help th,
.search-help td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #ccc;
  vertical-align: top;
}

.search-help thead td {
  font-weight: bold;
  background-color: #a898b7;
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.search-help tbody tr:nth-child(even) {
  background-color: #f0f0f0;
}

.search-help tbody tr:hover {
  background-color: #ddd;
}

.search-help td {
  word-wrap: break-word;
  max-width: 150px;
}

.search-help th {
  background-color: #444;
  color: white;
  font-weight: bold;
}

.search-help table td:first-child {
  font-weight: bold;
  white-space: nowrap;
}

.search-help td {
  padding: 6px;
  border-bottom: 1px solid #ddd;
}

.search-help .tips {
  margin-bottom: 4px;
  margin-top: -10px;
  margin-left: -10px;
  margin-right: -10px;
  padding: 8px;
  background: #333;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  color: #fff;
  font-size: 1.6em;
  text-align: center;
}

.search-help-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  margin-top: -10px;
  margin-left: -10px;
  margin-right: -10px;
  padding: 8px 12px;
  background: #333;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.search-help-header .tips {
  margin: 0;
  padding: 0;
  background: transparent;
  border-radius: 0;
  color: #fff;
  font-size: 1.6em;
  text-align: left;
}

/* Style for the close button */
.close-button {
  background-color: #a898b7;
  border: none;
  color: #eee;
  font-size: 18px;
  border-radius: 4px;
  cursor: pointer;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
}

.close-button:hover {
  background-color: #9386a4;
}

/* Overlay for the semi-transparent background */
.help-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  z-index: 5000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.bank-ledger-title {
  margin-bottom: 7px;
  /* font-weight: bold; */
  font-family: calibri;
  font-size: 1.1em;
  margin-bottom: 0px;
  width: 1262px;
  margin-left: 0px;
  margin-top: -2px;
  background: #6e5875;
  color: #fdfdfd;
  padding: 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.action-link {
  text-decoration: underline;
  color: #a080a8;
}

.selected-button {
  color: white;
  background-color: #696073;
  margin-left: 3px;
  border-radius: 4px;
  border: 1pt solid #634f69;
  padding: 4px;
  width: 29px;
}

.right-panel {
  position: absolute;
  top: 56px;
  left: 1330px;
  height: 814px;
  width: 750px;
}

.pdf-viewer {
  height: 100%;
  border: 1pt solid #999;
  border-radius: 5px;
}

.pdf-viewer-title {
  color: #eee;
  background: #6e5875;
  font-size: 1.3em;
  padding: 7px;
  border-top-left-radius: 7px;
  border-top-right-radius: 7px;
  padding-left: 20px;
}

.pdf-container {
  position: relative;
  height: 794px;
  padding: 10px;
  box-shadow: 9px 8px 7px 2px rgba(0, 0, 0, 0.2);
  background: #dddddd;
  border-bottom-right-radius: 5px;
  border-bottom-left-radius: 5px;
}

.pdf-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f3f3;
}

.pdf-loading-overlay .fa-spinner {
  font-size: 5em;
  color: #c0c0c0;
}

#pdf-frame[data-v-80ac8330] {
  border-radius: 6px;
}

.document-search {
  width: 400px;
  border-radius: 4px;
  border: 1pt solid #aaa;
  padding: 4px;
  outline: none;
}

.search-results {
  font-size: 0.8em;
  position: absolute;
  top: 40px; /* Adjust this value to position the results just below the input */
  left: 0;
  right: 0;
  background: rgba(51, 51, 51, 0.9); /* Semi-transparent background */
  color: #eee;
  z-index: 1000;
  max-height: 250px;
  overflow-y: auto;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.search-result {
  padding: 4px;
  padding-left: 10px;
  cursor: pointer;
}

.search-result:hover {
  background: #828282;
}

:deep(.highlight-doc) {
  background: #a080a8;
  color: black;
}

.automatch-popup {
  min-width: 280px;
  text-align: center;
}

.deposit-group-match-popup {
  width: 320px;
}

.deposit-group-match-popup h3 {
  margin: 0 0 8px;
  font-size: 1.05em;
  color: #333;
}

.deposit-group-match-popup p {
  margin: 0 0 12px;
  font-size: 0.95em;
  color: #444;
}

.deposit-group-match-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
  border: 1px solid #bbb;
  border-radius: 4px;
  font-size: 1em;
  color: #333;
}

.deposit-group-match-error {
  margin-top: 10px;
  color: #a33;
  font-size: 0.9em;
  text-align: left;
}

.confirm-popup {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 200px;
  transform: translate(-50%, -50%);
  background: #ffffff;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 7px 7px 8px 3px rgba(0, 0, 0, 0.2);
  z-index: 10020;
  border: 1px solid #888;
  color: #eee;
}

.confirm-message {
  font-size: 1em;
  margin-bottom: 20px;
  color: #333;
}
.confirm-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.confirm-buttons button {
  background: #a080a8;
  padding: 6px;
  width: 70px;
  color: #fff;
  border-radius: 4px;
  border: 1pt solid #ddd;
}

.bold-text {
  font-weight: bold;
  color: #ad76e3;
}

/* Verify tooltip styling */
.verify-tooltip {
  position: absolute;
  top: 40px;
  left: 50%;
  transform: translateX(-50%);
  background: #8a4590;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.9em;
  white-space: nowrap;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  animation: fadeInUp 0.3s ease;
}

.verify-tooltip .tooltip-close {
  margin-left: 8px;
  font-weight: bold;
  font-size: 1.2em;
  opacity: 0.7;
}

.verify-tooltip:hover .tooltip-close {
  opacity: 1;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}
</style>
